import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useReactToPrint } from 'react-to-print';
import { formatDateDDMMYYYY, formatDateTimeDDMMYYYY } from '../../utils/dateUtils';
import PatientCodeBarcodes from 'components/PatientCodeBarcodes';
import {
  Grid,
  Card,
  Typography,
  Box,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  styled,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Fab,
  Chip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Assignment as AssignmentIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  History as HistoryIcon,
  Science as ScienceIcon,
  Medication as MedicationIcon,
} from "@mui/icons-material";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import LoaderOverlay from "components/LoaderOverlay";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// API
import { api } from "../../api";
import irmHeader from 'assets/images/irm-header.png';

// Helper function to check if D&E should be disabled based on miscarriage type
const isDEDisabledForType = (miscarriageType) => {
  const disabledTypes = [
    'LUCS',
    'PTL',
    'RL Tubal Ectopic',
    'LT Tubal Ectopic',
    'Ectopic-B/L',
    'Normal Delivery'
  ];
  return disabledTypes.some(type => 
    miscarriageType?.toLowerCase().includes(type.toLowerCase())
  );
};

// Blood group options for ABORh test
const BLOOD_GROUP_OPTIONS = [
  { label: 'A Positive (A+)', value: 'A+' },
  { label: 'A Negative (A-)', value: 'A-' },
  { label: 'B Positive (B+)', value: 'B+' },
  { label: 'B Negative (B-)', value: 'B-' },
  { label: 'AB Positive (AB+)', value: 'AB+' },
  { label: 'AB Negative (AB-)', value: 'AB-' },
  { label: 'O Positive (O+)', value: 'O+' },
  { label: 'O Negative (O-)', value: 'O-' },
  { label: 'Bombay Blood Group (Oh)', value: 'Oh' },
];

// Helper function to get full blood group label from value
const getBloodGroupLabel = (value) => {
  const option = BLOOD_GROUP_OPTIONS.find(opt => opt.value === value);
  return option ? option.label : value;
};

// Param member slug for the Generic History "Comorbidities" rich-text field.
const COMORBIDITIES_SLUG = 'history-common-params-comorbidities';

// True when a rich-text value has no visible content (e.g. "<p><br></p>").
const isHtmlBlank = (html) => {
  if (!html) return true;
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim() === '';
};

// Collapse all Comorbidities entries from a contact_histories list into one
// HTML string (distinct values kept, joined with <br/>). "" -> caller shows NIL.
const extractComorbiditiesHtml = (histories = []) => {
  const values = [];
  histories.forEach((item) => {
    if (item?.param_group_member?.slug !== COMORBIDITIES_SLUG) return;
    const value = item.param_group_member_option?.value || item.value;
    if (!value || isHtmlBlank(value)) return;
    if (!values.includes(value)) values.push(value);
  });
  return values.join('<br/>');
};

// Helper function to format infertility type labels
const formatInfertilityType = (type) => {
  if (!type) return '-';
  
  const typeMap = {
    'primary': 'Primary Infertility',
    'secondary': 'Secondary Infertility',
    'boh': 'BOH',
    'gynecological': 'Gynecological Problems'
  };
  
  return typeMap[type.toLowerCase()] || type;
};

// Helper function to determine summary title based on infertility type
const getSummaryTitle = (obstetricHistory, spouseObstetricHistory) => {
  // Get the infertility type from patient first, then spouse
  const patientType = obstetricHistory?.infetrtility_type;
  const spouseType = spouseObstetricHistory?.infetrtility_type;
  const infertilityType = patientType || spouseType;
  
  if (!infertilityType) {
    return 'Treatment Summary';
  }
  
  const type = infertilityType.toLowerCase();
  
  switch (type) {
    case 'primary':
    case 'secondary':
      return 'Infertility Summary';
    case 'boh':
      return 'BOH Summary';
    case 'gynecological':
      return 'Gynecological Summary';
    default:
      return 'Treatment Summary';
  }
};

// Styled components for A4-like infertility summary design
const InfertilitySummaryContainer = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: '794px', // A4 width in pixels at 96dpi
  minHeight: 'auto',
  margin: '0 auto',
  padding: '20px',
  backgroundColor: '#ffffff',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
  fontSize: '12px',
  lineHeight: '1.4',
  color: '#333',
  position: 'relative',
  '@media print': {
    boxShadow: 'none',
    margin: '0 !important',
    padding: '5mm 5mm !important',
    paddingBottom: '10mm !important',
    marginBottom: '0px !important',
    minHeight: 'auto',
    pageBreakInside: 'auto',
    width: '100% !important',
    maxWidth: 'none !important',
    backgroundColor: '#ffffff !important',
    '-webkit-print-color-adjust': 'exact',
    'color-adjust': 'exact',
    fontSize: '14px !important',
    fontWeight: '500 !important',
    lineHeight: '1.4 !important',
    color: '#000000 !important',
    orphans: 3,
    widows: 3,
    '& .MuiGrid-container': {
      display: 'flex',
      flexDirection: 'row'
    },
    '& .MuiGrid-item': {
      display: 'flex',
      flexDirection: 'column'
    },
    '& *': {
      color: '#000000 !important',
      fontWeight: 'inherit !important',
    }
  },
}));

const HeaderImageSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: '20px',
  '& img': {
    width: '100%',
    maxWidth: '100%',
    height: 'auto',
    display: 'block',
  },
}));

const PatientInfoCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#f8f9fa',
  padding: '12px',
  borderRadius: '6px',
  marginBottom: '15px',
  border: '1px solid #e0e0e0',
}));

const SummaryHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '12px',
  paddingBottom: '8px',
  borderBottom: '2px solid #00798E',
}));

const SectionCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#f0f8ff',
  padding: '12px',
  borderRadius: '6px',
  marginBottom: '15px',
  border: '1px solid #b3d9ff',
  '@media print': {
    backgroundColor: 'transparent !important',
    border: 'none !important',
    borderRadius: '0 !important',
    pageBreakBefore: 'auto !important',
    pageBreakAfter: 'auto !important',
    pageBreakInside: 'auto !important',
    breakBefore: 'auto !important',
    breakAfter: 'auto !important',
    breakInside: 'auto !important',
    display: 'block',
    width: '100%',
    margin: '0 !important',
    marginBottom: '0 !important',
    padding: '4px 0 !important',
  },
  '& .section-title': {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#00798E',
    marginBottom: '12px',
    paddingBottom: '6px',
    borderBottom: '1px solid #d0e8f0',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    textAlign: 'left',
    '@media print': {
      pageBreakAfter: 'avoid',
      breakAfter: 'avoid',
      fontSize: '14px',
      marginBottom: '8px',
      paddingBottom: '4px',
      fontWeight: '700 !important',
    },
    '&::before': {
      content: '""',
      width: '3px',
      height: '14px',
      backgroundColor: '#4caf50',
      marginRight: '8px',
      borderRadius: '2px',
    },
  },
}));

const DataTable = styled(Table)(({ theme }) => ({
  marginTop: '8px',
  '@media print': {
    width: '100%',
    tableLayout: 'auto',
  },
  '& .MuiTableHead-root': {
    backgroundColor: '#f8f9fa',
    '@media print': {
      pageBreakAfter: 'avoid',
      breakAfter: 'avoid',
    },
  },
  '& .MuiTableCell-head': {
    fontSize: '11px',
    fontWeight: 'bold',
    color: '#00798E',
    padding: '8px',
    borderBottom: '2px solid #e0e0e0',
    '@media print': {
      padding: '4px',
      fontSize: '12px',
      fontWeight: '700 !important',
      pageBreakAfter: 'avoid',
      breakAfter: 'avoid',
    }
  },
  '& .MuiTableCell-body': {
    fontSize: '10px',
    padding: '6px 8px',
    borderBottom: '1px solid #e0e0e0',
    verticalAlign: 'top',
    '@media print': {
      padding: '4px',
      fontSize: '11px',
      fontWeight: '500',
      pageBreakInside: 'avoid',
      breakInside: 'avoid',
    },
  },
  '& .MuiTableRow-root': {
    '@media print': {
      pageBreakInside: 'avoid',
      breakInside: 'avoid',
    },
  },
}));

const WatermarkText = styled(Typography)(({ theme }) => ({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%) rotate(-45deg)',
  fontSize: '80px',
  fontWeight: 'bold',
  color: 'rgba(0, 121, 142, 0.05)',
  zIndex: 0,
  pointerEvents: 'none',
  userSelect: 'none',
  '@media print': {
    position: 'absolute',
    fontSize: '60px',
    color: 'rgba(0, 121, 142, 0.08) !important',
    '-webkit-print-color-adjust': 'exact',
    'color-adjust': 'exact',
  },
}));

const MainTitleSection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: '15px',
  padding: '8px 0',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '0',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '80px',
    height: '3px',
    background: theme.palette.grey[700],
    borderRadius: '2px',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '0',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '120px',
    height: '2px',
    background: theme.palette.grey[700],
    borderRadius: '1px',
  },
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  fontSize: '20px',
  fontWeight: 'bold',
  color: '#000000',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  marginTop: '6px',
  marginBottom: '6px',
  '@media print': {
    fontSize: '18px !important',
    color: '#000000 !important',
    fontWeight: 'bold !important',
    '-webkit-print-color-adjust': 'exact',
    'color-adjust': 'exact',
  },
}));

const PrintableFooter = styled(Box)(({ theme }) => ({
  width: '100%',
  borderTop: '2px solid #00798E',
  paddingTop: '8px',
  marginTop: '20px',
  textAlign: 'center',
  fontSize: '10px',
  color: '#333',
  lineHeight: '1.4',
  '@media print': {
    position: 'static',
    width: '100%',
    padding: '12px 0 8px 0',
    margin: '20px 0 0 0',
    borderTop: '2px solid #000000',
    backgroundColor: 'transparent',
    fontSize: '11px',
    fontWeight: '500',
    lineHeight: '1.3',
    color: '#000000 !important',
    '-webkit-print-color-adjust': 'exact',
    'color-adjust': 'exact',
    pageBreakBefore: 'auto',
    pageBreakAfter: 'auto',
    pageBreakInside: 'avoid',
    breakInside: 'avoid',
    minHeight: '60px',
  },
  '& .footer-address': {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    marginBottom: '4px',
    '@media print': {
      gap: '2px',
      marginBottom: '3px',
    }
  },
  '& .footer-line': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    '@media print': {
      gap: '6px',
    }
  },
  '& .phone-icon': {
    fontSize: '11px',
    verticalAlign: 'middle',
    marginRight: '2px',
    '@media print': {
      fontSize: '11px',
    }
  },
  '& .footer-emergency': {
    marginTop: '4px',
    fontWeight: '600',
    '@media print': {
      marginTop: '3px',
    }
  }
}));

function InfertilitySummary() {
  const navigate = useNavigate();
  const { patientCode, coupleCode } = useParams();
  const componentRef = useRef();

  // Helper function to render HTML content
  const renderHTMLContent = (htmlString) => {
    if (!htmlString) return '';
    // Check if content contains HTML tags
    if (htmlString.includes('<') && htmlString.includes('>')) {
      return <span dangerouslySetInnerHTML={{ __html: htmlString }} />;
    }
    return htmlString;
  };

  // State management
  const [loading, setLoading] = useState(false);
  const [patientInfo, setPatientInfo] = useState(null);
  const [visits, setVisits] = useState([]);
  const [selectedVisitNo, setSelectedVisitNo] = useState("");
  const [contactHistory, setContactHistory] = useState([]);
  const [loadingVisitData, setLoadingVisitData] = useState(false);
  const [obstetricHistory, setObstetricHistory] = useState(null);
  const [miscarriageHistories, setMiscarriageHistories] = useState([]);
  const [spouseObstetricHistory, setSpouseObstetricHistory] = useState(null);
  const [spouseMiscarriageHistories, setSpouseMiscarriageHistories] = useState([]);
  const [menstrualHistories, setMenstrualHistories] = useState([]);
  const [spouseMenstrualHistories, setSpouseMenstrualHistories] = useState([]);
  const [consultantName, setConsultantName] = useState('Dr. Default');
  const [consultantOccupation, setConsultantOccupation] = useState('');
  // Comorbidities is "standing" patient data — it always shows in the summary
  // regardless of the selected visit, so it is fetched across ALL visits
  // independently of loadVisitData(). "" means nothing recorded -> render NIL.
  const [patientComorbidities, setPatientComorbidities] = useState('');
  const [spouseComorbidities, setSpouseComorbidities] = useState('');

  // Load patient info on component mount
  useEffect(() => {
    if (patientCode) {
      loadPatientInfo();
    }
  }, [patientCode]);

  // Load visits when patient info is available
  useEffect(() => {
    if (patientInfo?.contact?.id) {
      loadVisits();
    }
  }, [patientInfo]);

  // Load contact history and prescription details when visit is selected
  useEffect(() => {
    if (patientInfo?.contact?.id && selectedVisitNo) {
      loadVisitData();
    }
  }, [selectedVisitNo, patientInfo]);

  // Comorbidities always reflects the latest value recorded in ANY visit, so
  // it is loaded once from the full (unfiltered) history — independent of the
  // visit dropdown.
  useEffect(() => {
    const loadComorbidities = async () => {
      const contactId = patientInfo?.contact?.id;
      if (!contactId) return;
      const spouse = getSpouseInfo(patientInfo);
      try {
        const calls = [api.opd.getContactHistory(contactId)];
        if (spouse?.id) calls.push(api.opd.getContactHistory(spouse.id));
        const [patientRes, spouseRes] = await Promise.all(calls);
        setPatientComorbidities(
          extractComorbiditiesHtml(patientRes?.data?.contact_histories || []),
        );
        setSpouseComorbidities(
          spouse?.id
            ? extractComorbiditiesHtml(spouseRes?.data?.contact_histories || [])
            : '',
        );
      } catch (error) {
        console.error('Error loading comorbidities:', error);
        setPatientComorbidities('');
        setSpouseComorbidities('');
      }
    };
    loadComorbidities();
  }, [patientInfo]);

  const loadPatientInfo = async () => {
    try {
      setLoading(true);
      const response = await api.contacts.getPatientDetailsByExternalCode(patientCode);
      if (response?.data) {
        setPatientInfo(response.data);
        // Fetch last prescription to get consultant name using contact ID
        if (response.data.contact?.id) {
          await fetchLastPrescription(response.data.contact.id);
        }
      }
    } catch (error) {
      console.error("Error loading patient info:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLastPrescription = async (contactId) => {
    try {
      const response = await api.opd.getPrescriptions(contactId, { lastOnly: 'true' });
      if (response?.data && response.data.length > 0) {
        // Get the latest prescription (first one when lastOnly is true)
        const lastPrescription = response.data[0];
        // Get consultant name and occupation from doctor object
        const consultantName = lastPrescription.doctor?.name || 
                               lastPrescription.doctor_name || 
                               'Dr. Default';
        const occupation = lastPrescription.doctor?.occupation || '';
        setConsultantName(consultantName);
        setConsultantOccupation(occupation);
      } else {
        setConsultantName('Dr. Default');
        setConsultantOccupation('');
      }
    } catch (error) {
      console.error("Error fetching last prescription:", error);
      setConsultantName('Dr. Default');
      setConsultantOccupation('');
    }
  };

  const loadVisits = async () => {
    try {
      const response = await api.contacts.getAllVisitsByContact(patientInfo.contact.id);
      if (response?.data) {
        // Sort visits by visit_no in descending order (latest first)
        const sortedVisits = response.data.sort((a, b) => b.visit_no - a.visit_no);
        setVisits(sortedVisits);
        // Auto-select the latest visit if available
        if (sortedVisits.length > 0) {
          setSelectedVisitNo(sortedVisits[0].visit_no.toString().padStart(4, '0'));
        }
      }
    } catch (error) {
      console.error("Error loading visits:", error);
    }
  };

  const loadVisitData = async () => {
    try {
      setLoadingVisitData(true);
      const contactId = patientInfo.contact.id;
      const patientCode = patientInfo.contact.external_code;
      const spouseInfo = getSpouseInfo(patientInfo);
      
      console.log('Loading visit data for:', { contactId, patientCode, selectedVisitNo, spouseInfo });
      
      if (selectedVisitNo === "ALL_VISITS") {
        // Fetch all records without visitNo filter
        console.log('Fetching all visit data...');
        
        // Prepare API calls for patient
        const apiCalls = [
          api.opd.getContactHistory(contactId)
        ];
        
        // Add spouse history if spouse exists
        if (spouseInfo?.id) {
          console.log('Fetching spouse history for:', spouseInfo.id);
          apiCalls.push(api.opd.getContactHistory(spouseInfo.id));
        }
        
        const responses = await Promise.all(apiCalls);
        const [historyResponse, spouseHistoryResponse] = responses;
        
        console.log('All visits - Patient History response:', historyResponse?.data);
        if (spouseHistoryResponse) {
          console.log('All visits - Spouse History response:', spouseHistoryResponse?.data);
        }
        
        // Extract contact_histories from response data
        const patientHistory = historyResponse?.data?.contact_histories || [];
        const spouseHistory = spouseHistoryResponse?.data?.contact_histories || [];
        const combinedHistory = [
          ...patientHistory.map(item => ({ ...item, source: 'Patient' })),
          ...spouseHistory.map(item => ({ ...item, source: 'Spouse' }))
        ];
        
        // Extract obstetric and miscarriage history data
        setObstetricHistory(historyResponse?.data?.obsteric_history || null);
        setMiscarriageHistories(historyResponse?.data?.miscarriage_histories || []);
        setSpouseObstetricHistory(spouseHistoryResponse?.data?.obsteric_history || null);
        setSpouseMiscarriageHistories(spouseHistoryResponse?.data?.miscarriage_histories || []);
        
        // Extract menstrual history data from contact_histories
        const extractMenstrualHistory = (histories) => {
          const menstrualRecords = histories.filter(history => {
            const slug = history.param_group_member?.slug;
            return slug && slug.startsWith('menstrual-history-params-');
          });
          
          // Group records by private_note (which contains the grouping timestamp)
          const groupedRecords = {};
          menstrualRecords.forEach(record => {
            const groupKey = record.private_note || 'default';
            if (!groupedRecords[groupKey]) {
              groupedRecords[groupKey] = {};
            }
            
            const slug = record.param_group_member?.slug;
            let fieldName = '';
            
            switch (slug) {
              case 'menstrual-history-params-lmp':
                fieldName = 'lmp';
                break;
              case 'menstrual-history-params-menarche':
                fieldName = 'menarche';
                break;
              case 'menstrual-history-params-cycle-length':
                fieldName = 'cycleLength';
                break;
              case 'menstrual-history-params-duration':
                fieldName = 'duration';
                break;
              case 'menstrual-history-params-flow':
                fieldName = 'flow';
                break;
              case 'menstrual-history-params-pain':
                fieldName = 'pain';
                break;
              case 'menstrual-history-params-others':
                fieldName = 'other';
                break;
              default:
                fieldName = '';
                break;
            }
            
            if (fieldName) {
              groupedRecords[groupKey][fieldName] = record.value && record.value !== 'NA' ? record.value : '';
            }
          });
          
          return Object.values(groupedRecords).map(group => ({
            lmp: group.lmp || '',
            menarche: group.menarche || '',
            cycleLength: group.cycleLength || '',
            duration: group.duration || '',
            flow: group.flow || '',
            pain: group.pain || '',
            other: group.other || ''
          }));
        };
        
        setMenstrualHistories(extractMenstrualHistory(patientHistory));
        setSpouseMenstrualHistories(spouseHistory.length > 0 ? extractMenstrualHistory(spouseHistory) : []);
        
        setContactHistory(combinedHistory);
      } else {
        // Fetch records filtered by visitNo
        const visitNumber = parseInt(selectedVisitNo);
        console.log('Fetching visit-specific data for visit:', visitNumber);
        
        // Prepare API calls for patient with visitNo query param
        const apiCalls = [
          api.opd.getContactHistory(contactId, { visitNo: visitNumber })
        ];
        
        // Add spouse history with visitNo if spouse exists
        if (spouseInfo?.id) {
          console.log('Fetching spouse history for visit:', { spouseId: spouseInfo.id, visitNumber });
          apiCalls.push(api.opd.getContactHistory(spouseInfo.id, { visitNo: visitNumber }));
        }
        
        const responses = await Promise.all(apiCalls);
        const [historyResponse, spouseHistoryResponse] = responses;
        
        console.log('Visit-specific - Patient History response:', historyResponse?.data);
        if (spouseHistoryResponse) {
          console.log('Visit-specific - Spouse History response:', spouseHistoryResponse?.data);
        }
        
        // Extract contact_histories from response data
        const patientHistory = historyResponse?.data?.contact_histories || [];
        const spouseHistory = spouseHistoryResponse?.data?.contact_histories || [];
        const combinedHistory = [
          ...patientHistory.map(item => ({ ...item, source: 'Patient' })),
          ...spouseHistory.map(item => ({ ...item, source: 'Spouse' }))
        ];
        
        // Extract obstetric and miscarriage history data
        setObstetricHistory(historyResponse?.data?.obsteric_history || null);
        setMiscarriageHistories(historyResponse?.data?.miscarriage_histories || []);
        setSpouseObstetricHistory(spouseHistoryResponse?.data?.obsteric_history || null);
        setSpouseMiscarriageHistories(spouseHistoryResponse?.data?.miscarriage_histories || []);
        
        // Extract menstrual history data from contact_histories (same function as above)
        const extractMenstrualHistory = (histories) => {
          const menstrualRecords = histories.filter(history => {
            const slug = history.param_group_member?.slug;
            return slug && slug.startsWith('menstrual-history-params-');
          });
          
          const groupedRecords = {};
          menstrualRecords.forEach(record => {
            const groupKey = record.private_note || 'default';
            if (!groupedRecords[groupKey]) {
              groupedRecords[groupKey] = {};
            }
            
            const slug = record.param_group_member?.slug;
            let fieldName = '';
            
            switch (slug) {
              case 'menstrual-history-params-lmp':
                fieldName = 'lmp';
                break;
              case 'menstrual-history-params-menarche':
                fieldName = 'menarche';
                break;
              case 'menstrual-history-params-cycle-length':
                fieldName = 'cycleLength';
                break;
              case 'menstrual-history-params-duration':
                fieldName = 'duration';
                break;
              case 'menstrual-history-params-flow':
                fieldName = 'flow';
                break;
              case 'menstrual-history-params-pain':
                fieldName = 'pain';
                break;
              case 'menstrual-history-params-others':
                fieldName = 'other';
                break;
              default:
                fieldName = '';
                break;
            }
            
            if (fieldName) {
              groupedRecords[groupKey][fieldName] = record.value && record.value !== 'NA' ? record.value : '';
            }
          });
          
          return Object.values(groupedRecords).map(group => ({
            lmp: group.lmp || '',
            menarche: group.menarche || '',
            cycleLength: group.cycleLength || '',
            duration: group.duration || '',
            flow: group.flow || '',
            pain: group.pain || '',
            other: group.other || ''
          }));
        };
        
        setMenstrualHistories(extractMenstrualHistory(patientHistory));
        setSpouseMenstrualHistories(spouseHistory.length > 0 ? extractMenstrualHistory(spouseHistory) : []);
        
        setContactHistory(combinedHistory);
      }
    } catch (error) {
      console.error("Error loading visit data:", error);
      setContactHistory([]);
    } finally {
      setLoadingVisitData(false);
    }
  };

  // Print functionality
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Infertility_Summary_${patientCode}_Visit_${selectedVisitNo}`,
    pageStyle: `
      @page {
        size: A4;
        margin: 4mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        * {
          color: #000000 !important;
          font-weight: 500 !important;
        }
      }
    `,
    onBeforeGetContent: () => {
      return Promise.resolve();
    },
    onAfterPrint: () => {
      console.log('Print completed');
    },
    removeAfterPrint: true,
  });

  // Custom print handler with instructions
  const handlePrintWithInstructions = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    let message = '📋 Important: Remove Headers & Footers\n\n';
    
    if (userAgent.includes('chrome') || userAgent.includes('edge')) {
      message += '✓ Chrome/Edge Users:\n';
      message += '1. Click "More settings" in print dialog\n';
      message += '2. UNCHECK "Headers and footers"\n';
      message += '3. Then print or save as PDF\n\n';
      message += 'This removes URL, date, and page numbers.';
    } else if (userAgent.includes('firefox')) {
      message += '✓ Firefox Users:\n';
      message += '1. File → Page Setup\n';
      message += '2. Margins & Header/Footer tab\n';
      message += '3. Set all dropdowns to "blank"\n';
      message += '4. Click OK, then print\n\n';
      message += 'This removes URL, date, and page numbers.';
    } else {
      message += 'In your print dialog:\n';
      message += '• Find "Headers and footers" option\n';
      message += '• UNCHECK or DISABLE it\n\n';
      message += 'This removes URL, date, and page numbers.';
    }
    
    message += '\n\nClick OK to continue to print dialog.';
    
    // eslint-disable-next-line no-restricted-globals
    if (confirm(message)) {
      handlePrint();
    }
  };

  const formatDate = (dateString) => {
    return formatDateDDMMYYYY(dateString) || 'N/A';
  };

  const formatDateTime = (dateString) => {
    return formatDateTimeDDMMYYYY(dateString) || 'N/A';
  };

  const getSelectedVisit = () => {
    return visits.find(v => v.visit_no.toString().padStart(4, '0') === selectedVisitNo);
  };

  const getSpouseInfo = (patientInfo) => {
    if (!patientInfo) return null;
    
    // Don't show spouse info if patient is single or divorced (but show for widowed)
    const maritalStatus = patientInfo?.contact?.marital_status;
    if (maritalStatus === 'single' || maritalStatus === 'divorced') {
      return null;
    }
    
    // Try different possible paths for spouse information
    const spousePaths = [
      patientInfo?.spouse?.contact,       // New path based on API response
      patientInfo?.spouse,                // Direct spouse object
      patientInfo?.spouseContact,
      patientInfo?.patientCouple?.spouse_contact,
      patientInfo?.spouseCouple?.contact,
      patientInfo?.couple?.spouse_contact,
    ];

    for (const spouse of spousePaths) {
      if (spouse && spouse.full_name) {
        return spouse;
      }
    }

    return null;
  };

  const spouseInfo = getSpouseInfo(patientInfo);
  const hasSpouse = !!spouseInfo;
  const hasMarriedFor = !!patientInfo?.contact?.married_for;
  const hasTryingFor = !!patientInfo?.contact?.trying_for;

  // Male-specific data (semen analysis, male investigations) belongs to
  // whichever party is actually male - the patient or the spouse - not
  // always the spouse. Defaults to 'Spouse' when the patient's gender
  // isn't recorded as male, matching the previous behavior.
  const patientGenderCode = patientInfo?.contact?.gender;
  const spouseGenderCode = spouseInfo?.gender;
  const maleSource = patientGenderCode === 'M' ? 'Patient' : 'Spouse';

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <LoaderOverlay open={loading || loadingVisitData} />
      
      {/* Print-specific styles for footer on every page */}
      <style>
        {`
          @media print {
            @page {
              margin: 4mm;
              size: A4;
            }
            
            body {
              margin: 0;
              padding: 0;
              font-size: 14px !important;
            }
            
            /* Remove section/container-level page breaks */
            .MuiBox-root, div[class*="SectionCard"] {
              page-break-before: auto !important;
              page-break-after: auto !important;
            }
            
            /* Ensure tables and content flow naturally */
            table, .MuiTable-root, .MuiTableContainer-root {
              page-break-inside: auto !important;
              break-inside: auto !important;
            }
            
            /* Allow Grid containers to flow */
            .MuiGrid-container, .MuiGrid-item {
              page-break-before: auto !important;
              page-break-after: auto !important;
            }
            
            /* Specific rules for print container */
            .print-container {
              page-break-before: auto !important;
              page-break-after: auto !important;
            }
            
            .print-container > * {
              page-break-before: auto !important;
            }
            
            /* Prevent section headers from creating breaks */
            .section-title, h1, h2, h3, h4, h5, h6 {
              page-break-before: auto !important;
              page-break-after: avoid !important;
            }
            
            /* Preserve colors but ensure print quality */
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            /* Ensure headings and labels keep their colors but are bold */
            h1, h2, h3, h4, h5, h6, strong, b {
              font-weight: 700 !important;
            }
            
            /* Keep table header colors but make them bold */
            .MuiTableCell-head {
              font-weight: 700 !important;
            }
            
            /* Ensure section titles keep their teal color */
            .section-title {
              color: #00798E !important;
              font-weight: 700 !important;
            }
            
            /* Patient Information Labels */
            .patient-info-label {
              color: #000000 !important;
              font-weight: 600 !important;
            }
            
            /* Make data value cells (fontSize 11px) darker and bold */
            .MuiTableCell-root[style*="font-size: 11px"],
            .MuiTableCell-root[style*="fontSize: 11px"] {
              color: #000000 !important;
              font-weight: 500 !important;
            }
            
            /* Force natural content flow - remove all page breaks */
            * {
              page-break-before: auto !important;
              page-break-after: auto !important;
              page-break-inside: auto !important;
              break-before: auto !important;
              break-after: auto !important;
              break-inside: auto !important;
            }
            
            /* Specific overrides for Material-UI components */
            .MuiGrid-container, .MuiGrid-item {
              page-break-before: auto !important;
              page-break-after: auto !important;
              page-break-inside: auto !important;
              break-before: auto !important;
              break-after: auto !important;
              break-inside: auto !important;
            }
            
            .MuiTableContainer-root {
              page-break-before: auto !important;
              page-break-after: auto !important;
              page-break-inside: auto !important;
              break-before: auto !important;
              break-after: auto !important;
              break-inside: auto !important;
            }
            
            .MuiTable-root {
              page-break-before: auto !important;
              page-break-after: auto !important;
              page-break-inside: auto !important;
              break-before: auto !important;
              break-after: auto !important;
              break-inside: auto !important;
            }
            
            /* Remove any section breaks */
            div, section, article {
              page-break-before: auto !important;
              page-break-after: auto !important;
            }

            /* PRINT STYLING - Clean, Professional Black & White */
            /* Remove all background colors - override inline styles */
            * {
              background-color: transparent !important;
              background: none !important;
              color: #000000 !important;
            }

            /* Typography - clean headings */
            h1, h2, h3, h4, h5, h6,
            .MuiTypography-h6,
            .MuiTypography-subtitle1,
            .MuiTypography-subtitle2 {
              font-weight: 700 !important;
              border: none !important;
              padding: 6px 0 !important;
              margin: 10px 0 6px 0 !important;
              font-size: 13px !important;
            }

            /* Section title - remove colored bar */
            .section-title::before {
              display: none !important;
            }

            .section-title {
              color: #000000 !important;
            }

            .party-title {
              font-weight: 800 !important;
              text-decoration: underline !important;
              text-underline-offset: 4px !important;
            }

            .section-subtitle {
              font-weight: 700 !important;
              text-decoration: underline !important;
              text-underline-offset: 3px !important;
            }

            /* Tables - minimal, professional lines */
            .MuiTable-root {
              width: 100% !important;
              border-collapse: collapse !important;
            }

            .MuiTableCell-head {
              font-weight: 700 !important;
              border: none !important;
              border-bottom: 1px solid #000000 !important;
              padding: 6px 4px !important;
              background-color: transparent !important;
              font-size: 12px !important;
            }

            .MuiTableCell-body {
              font-size: 12.5px !important;
              font-weight: 500 !important;
              border: none !important;
              border-bottom: 1px solid #d0d0d0 !important;
              padding: 6px 4px !important;
            }

            .MuiTableRow-root:last-child .MuiTableCell-body {
              border-bottom: 1px solid #000000 !important;
            }

            .patient-info-label {
              font-weight: 700 !important;
              border: none !important;
              border-bottom: 1px solid #d0d0d0 !important;
            }

            .patient-info-label.strong {
              font-weight: 800 !important;
              letter-spacing: 0.2px !important;
            }

            .patient-info-value {
              font-weight: 400 !important;
            }

            /* Remove heavy borders, shadows and boxes */
            .MuiBox-root,
            .MuiPaper-root,
            .MuiCard-root,
            .MuiTableContainer-root {
              box-shadow: none !important;
              border: none !important;
            }

            .MuiBox-root {
              padding: 6px 0 !important;
              margin-bottom: 6px !important;
            }

            /* Hide interactive elements */
            button,
            .MuiIconButton-root,
            .MuiButton-root {
              display: none !important;
            }

            /* Chips - subtle outline only */
            .MuiChip-root {
              border: 1px solid #999999 !important;
            }

            /* Remove any colored background from styled components */
            [style*="backgroundColor"] {
              background-color: transparent !important;
            }
          }
        `}
      </style>
      
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                bgColor="logo"
                borderRadius="lg"
                coloredShadow="logo"
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center">
                    <IconButton
                      onClick={() => navigate(-1)}
                      sx={{ color: 'white', mr: 1 }}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                    <MDTypography variant="h6" color="white">
                      {getSummaryTitle(obstetricHistory, spouseObstetricHistory)}
                    </MDTypography>
                  </Box>
                  
                  {/* Shortcut Navigation Buttons */}
                  <Box display="flex" gap={1}>
                    <Tooltip title="Manage Contact Histories" placement="top" 
                      sx={{ 
                        '& .MuiTooltip-tooltip': { 
                          backgroundColor: '#333', 
                          color: 'white',
                          fontSize: '0.75rem'
                        } 
                      }}>
                      <IconButton
                        onClick={() => navigate(`/contact-histories/${patientCode}/${coupleCode || patientCode}`)}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          color: 'white',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '50%',
                          width: '40px',
                          height: '40px',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                          },
                          transition: 'all 0.2s ease-in-out',
                          '& .MuiSvgIcon-root': {
                            color: 'white'
                          }
                        }}
                      >
                        <HistoryIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Manage Investigations" placement="top"
                      sx={{ 
                        '& .MuiTooltip-tooltip': { 
                          backgroundColor: '#333', 
                          color: 'white',
                          fontSize: '0.75rem'
                        } 
                      }}>
                      <IconButton
                        onClick={() => navigate(`/contact-investigations/${patientCode}/${coupleCode || patientCode}`)}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          color: 'white',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '50%',
                          width: '40px',
                          height: '40px',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                          },
                          transition: 'all 0.2s ease-in-out',
                          '& .MuiSvgIcon-root': {
                            color: 'white'
                          }
                        }}
                      >
                        <ScienceIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Patient Prescriptions" placement="top"
                      sx={{ 
                        '& .MuiTooltip-tooltip': { 
                          backgroundColor: '#333', 
                          color: 'white',
                          fontSize: '0.75rem'
                        } 
                      }}>
                      <IconButton
                        onClick={() => navigate(`/prescription/patient/${patientCode}/${coupleCode || patientCode}`)}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          color: 'white',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '50%',
                          width: '40px',
                          height: '40px',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                          },
                          transition: 'all 0.2s ease-in-out',
                          '& .MuiSvgIcon-root': {
                            color: 'white'
                          }
                        }}
                      >
                        <MedicationIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </MDBox>

              {/* Patient Info Summary */}
              {patientInfo && (
                <MDBox p={2} sx={{ backgroundColor: 'rgba(0, 121, 142, 0.05)' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        Patient Name
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {patientInfo?.contact?.full_name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        Patient Code
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {patientInfo?.contact?.external_code}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        Gender
                      </Typography>
                      <Typography variant="body1">
                        {patientInfo?.contact?.gender}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        Couple Code
                      </Typography>
                      <Typography variant="body1">
                        {coupleCode !== 'undefined' ? coupleCode : 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </MDBox>
              )}

              {/* Visit Selection */}
              <MDBox p={3} pt={2}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel 
                        sx={{ 
                          color: '#00798E',
                          '&.Mui-focused': { color: '#00798E' }
                        }}
                      >
                        Select Visit
                      </InputLabel>
                      <Select
                        value={selectedVisitNo}
                        label="Select Visit"
                        onChange={(e) => {
                          setSelectedVisitNo(e.target.value);
                          // Clear previous data when changing visit
                          setContactHistory([]);
                        }}
                        sx={{
                          height: 45,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#00798E',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#00798E',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#00798E',
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em>Select a visit</em>
                        </MenuItem>
                        <MenuItem value="ALL_VISITS">
                          <Box display="flex" alignItems="center">
                            <AssignmentIcon sx={{ mr: 1, fontSize: '1rem', color: '#00798E' }} />
                            <strong>All Visits (Export Report)</strong>
                          </Box>
                        </MenuItem>
                        {visits.map((visit) => (
                          <MenuItem key={visit.id} value={visit.visit_no.toString().padStart(4, '0')}>
                            Visit #{visit.visit_no.toString().padStart(4, '0')} - {formatDate(visit.visit_date)} ({visit.visit_type})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    {loadingVisitData && (
                      <Box display="flex" alignItems="center" mb={2}>
                        <Typography variant="body2" color="textSecondary">
                          Loading visit data...
                        </Typography>
                      </Box>
                    )}
                    {selectedVisitNo && (
                      <Typography variant="body2" color="textSecondary">
                        {selectedVisitNo === "ALL_VISITS" ? (
                          <>
                            Selected: <strong>All Visits</strong> • 
                            Total {visits.length} visits • 
                            Export comprehensive report
                          </>
                        ) : (
                          <>
                            Selected: Visit #{selectedVisitNo} • 
                            {visits.find(v => v.visit_no.toString().padStart(4, '0') === selectedVisitNo)?.status} • 
                            {visits.find(v => v.visit_no.toString().padStart(4, '0') === selectedVisitNo)?.visit_type}
                          </>
                        )}
                      </Typography>
                    )}
                    
                    {/* Visit Details Summary */}
                    {selectedVisitNo && selectedVisitNo !== "ALL_VISITS" && visits.find(v => v.visit_no.toString().padStart(4, '0') === selectedVisitNo) && (
                      <Box mt={2}>
                        <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                          Visit Details:
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                              Visit Date
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {formatDate(visits.find(v => v.visit_no.toString().padStart(4, '0') === selectedVisitNo)?.visit_date)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                              Visit Type
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {visits.find(v => v.visit_no.toString().padStart(4, '0') === selectedVisitNo)?.visit_type}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                              Status
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {visits.find(v => v.visit_no.toString().padStart(4, '0') === selectedVisitNo)?.status}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                              Close Date
                            </Typography>
                            {visits.find(v => v.visit_no.toString().padStart(4, '0') === selectedVisitNo)?.visit_end_date ? (
                              <Typography variant="body2" fontWeight="bold">
                                {formatDate(visits.find(v => v.visit_no.toString().padStart(4, '0') === selectedVisitNo)?.visit_end_date)}
                              </Typography>
                            ) : visits.find(v => v.visit_no.toString().padStart(4, '0') === selectedVisitNo)?.status === 'ACTIVE' ? (
                              <Typography variant="body2" color="success.main" fontWeight="bold">
                                Visit is currently active
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                Not closed
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </MDBox>

              {/* Main Content Area */}
              <MDBox p={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    {selectedVisitNo ? (
                      // A4 Design for Individual Visit Summary or All Visits
                      <InfertilitySummaryContainer ref={componentRef} elevation={3} className="print-container">
                        {/* Header Image */}
                        <HeaderImageSection>
                          <img src={irmHeader} alt="IRM Header" />
                        </HeaderImageSection>

                        {/* Main Title Section */}
                        <MainTitleSection>
                          <MainTitle>
                            {getSummaryTitle(obstetricHistory, spouseObstetricHistory)}
                          </MainTitle>
                        </MainTitleSection>

                        {/* Consultant Information + Patient/Couple/Spouse barcodes */}
                        <Box sx={{
                          marginTop: '12px',
                          marginBottom: '16px',
                          display: 'flex',
                          alignItems: 'flex-start',
                        }}>
                          <Box sx={{ width: '65%', textAlign: 'left', padding: '8px' }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontSize: '15px',
                                fontWeight: 600,
                                color: theme => theme.palette.grey[800],
                                fontStyle: 'normal',
                              }}
                            >
                              {consultantName}
                            </Typography>
                            {consultantOccupation && (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: '13px',
                                  color: theme => theme.palette.grey[600],
                                  fontStyle: 'italic',
                                  marginTop: '4px',
                                }}
                              >
                                {consultantOccupation}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ width: '35%', display: 'flex', justifyContent: 'flex-end', px: 1, pt: 1 }}>
                            <PatientCodeBarcodes
                              size="large"
                              layout="grid2"
                              patientCode={patientInfo?.contact?.external_code || patientCode}
                              coupleCode={coupleCode !== 'undefined' ? coupleCode : undefined}
                              spouseCode={getSpouseInfo(patientInfo)?.external_code}
                            />
                          </Box>
                        </Box>

                        {/* Patient & Spouse Information Table */}
                        <PatientInfoCard>
                          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                            <DataTable size="small">
                              <TableBody>
                                <TableRow>
                                  <TableCell className="patient-info-label strong" sx={{ fontWeight: 'bold', color: '#128296', width: '20%', fontSize: '10px' }}>
                                    Patient ID
                                  </TableCell>
                                  <TableCell className="patient-info-value" sx={{ fontSize: '11px', width: '30%' }}>
                                    {patientInfo?.contact?.external_code || patientCode}
                                  </TableCell>
                                  <TableCell className="patient-info-label strong" sx={{ fontWeight: 'bold', color: '#128296', width: '20%', fontSize: '10px' }}>
                                    Date of Enrollment
                                  </TableCell>
                                  <TableCell className="patient-info-value" sx={{ fontSize: '11px', width: '30%' }}>
                                    {formatDate(patientInfo?.contact?.created_at)}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell className="patient-info-label strong" sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px' }}>
                                    Patient Name
                                  </TableCell>
                                  <TableCell className="patient-info-value" sx={{ fontSize: '11px' }} colSpan={hasSpouse ? 1 : 3}>
                                    {patientInfo?.contact?.full_name || 'N/A'}
                                  </TableCell>
                                  {hasSpouse && (
                                    <>
                                      <TableCell className="patient-info-label strong" sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px' }}>
                                        Spouse Name
                                      </TableCell>
                                      <TableCell className="patient-info-value" sx={{ fontSize: '11px' }}>
                                        {spouseInfo?.full_name || 'N/A'}
                                      </TableCell>
                                    </>
                                  )}
                                </TableRow>
                                <TableRow>
                                  <TableCell className="patient-info-label strong" sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px' }}>
                                    Age
                                  </TableCell>
                                  <TableCell className="patient-info-value" sx={{ fontSize: '11px' }} colSpan={hasSpouse ? 1 : 3}>
                                    {patientInfo?.contact?.age || 'N/A'}
                                  </TableCell>
                                  {hasSpouse && (
                                    <>
                                      <TableCell className="patient-info-label strong" sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px' }}>
                                        Age
                                      </TableCell>
                                      <TableCell className="patient-info-value" sx={{ fontSize: '11px' }}>
                                        {spouseInfo?.age || 'N/A'}
                                      </TableCell>
                                    </>
                                  )}
                                </TableRow>
                                {(hasMarriedFor || hasTryingFor) && (
                                  <TableRow>
                                    {hasMarriedFor && hasTryingFor ? (
                                      <>
                                        <TableCell className="patient-info-label strong" sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px' }}>
                                          Marriage Duration
                                        </TableCell>
                                        <TableCell className="patient-info-value" sx={{ fontSize: '11px' }}>
                                          {patientInfo?.contact?.married_for}
                                        </TableCell>
                                        <TableCell className="patient-info-label strong" sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px' }}>
                                          Trying for Pregnancy
                                        </TableCell>
                                        <TableCell className="patient-info-value" sx={{ fontSize: '11px' }}>
                                          {patientInfo?.contact?.trying_for}
                                        </TableCell>
                                      </>
                                    ) : hasMarriedFor ? (
                                      <>
                                        <TableCell className="patient-info-label strong" sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px' }}>
                                          Marriage Duration
                                        </TableCell>
                                        <TableCell className="patient-info-value" sx={{ fontSize: '11px' }} colSpan={3}>
                                          {patientInfo?.contact?.married_for}
                                        </TableCell>
                                      </>
                                    ) : (
                                      <>
                                        <TableCell className="patient-info-label strong" sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px' }}>
                                          Trying for Pregnancy
                                        </TableCell>
                                        <TableCell className="patient-info-value" sx={{ fontSize: '11px' }} colSpan={3}>
                                          {patientInfo?.contact?.trying_for}
                                        </TableCell>
                                      </>
                                    )}
                                  </TableRow>
                                )}
                                <TableRow>
                                  <TableCell className="patient-info-label strong" sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px' }}>
                                    Presenting Problem
                                  </TableCell>
                                  <TableCell className="patient-info-value" colSpan={3} sx={{ fontSize: '11px', fontWeight: '600', color: '#00798E' }}>
                                    {(() => {
                                      // Get the latest infertility type from patient or spouse obstetric history
                                      const patientInfertilityType = obstetricHistory?.infetrtility_type;
                                      const spouseInfertilityType = spouseObstetricHistory?.infetrtility_type;
                                      
                                      // Priority: Patient first, then spouse, then "Not Mentioned"
                                      const latestInfertilityType = patientInfertilityType || spouseInfertilityType;
                                      
                                      // Get remarks from patient or spouse
                                      const patientRemarks = obstetricHistory?.remarks;
                                      const spouseRemarks = spouseObstetricHistory?.remarks;
                                      const remarks = patientRemarks || spouseRemarks;
                                      
                                      // Check if remarks has valid value
                                      const hasRemarks = remarks && remarks.trim() && remarks.trim() !== '';
                                      
                                      const infertilityText = latestInfertilityType ? formatInfertilityType(latestInfertilityType) : 'Not Mentioned';
                                      
                                      return (
                                        <>
                                          {infertilityText}
                                          {hasRemarks && (
                                            <span style={{ fontWeight: 'normal', color: '#666', marginLeft: '8px' }}>
                                              (<span dangerouslySetInnerHTML={{ __html: remarks }} />)
                                            </span>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </DataTable>
                          </TableContainer>
                        </PatientInfoCard>

                        {/* Summary Information */}
                        {/* <PatientInfoCard>
                          <Typography variant="body2" sx={{ fontSize: '10px', color: '#00798E', fontWeight: 'bold', mb: 1 }}>
                            {selectedVisitNo === "ALL_VISITS" ? (
                              <>
                                Comprehensive Summary - All {visits.length} Visits | 
                                Date Range: {visits.length > 0 && `${formatDate(visits[visits.length - 1]?.visit_date)} - ${formatDate(visits[0]?.visit_date)}`}
                              </>
                            ) : (
                              <>
                                Visit #{selectedVisitNo} Summary | 
                                {getSelectedVisit() && (
                                  <>
                                    Visit Date: {formatDate(getSelectedVisit()?.visit_date)} | 
                                    Type: {getSelectedVisit()?.visit_type} | 
                                    Status: {getSelectedVisit()?.status}
                                    {getSelectedVisit()?.visit_end_date && (
                                      <> | Close Date: {formatDate(getSelectedVisit()?.visit_end_date)}</>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '9px' }}>
                            Generated: {formatDateTime(new Date())}
                            {selectedVisitNo === "ALL_VISITS" && contactHistory.length > 0 && (
                              <> | Medical History Records: {contactHistory.length}</>
                            )}
                          </Typography>
                        </PatientInfoCard> */}





                        {/* Obstetric History Section */}
                        {(() => {
                          // Check if there's any miscarriage data or infertility information
                          const hasPatientMiscarriage = miscarriageHistories && miscarriageHistories.length > 0;
                          const hasSpouseMiscarriage = spouseMiscarriageHistories && spouseMiscarriageHistories.length > 0;
                          const miscarriageExists = hasPatientMiscarriage || hasSpouseMiscarriage;
                          
                          const hasPatientInfertility = obstetricHistory;
                          const hasSpouseInfertility = spouseObstetricHistory;
                          const infertilityExists = hasPatientInfertility || hasSpouseInfertility;
                          
                          // Check if infertility type is "primary" - if so, hide obstetric history
                          const patientType = obstetricHistory?.infetrtility_type?.toLowerCase();
                          const spouseType = spouseObstetricHistory?.infetrtility_type?.toLowerCase();
                          const isPrimary = patientType === 'primary' || spouseType === 'primary';
                          
                          // Check if para values exist and have values greater than 0
                          const patientParaP1 = parseInt(obstetricHistory?.para_p1) || 0;
                          const patientParaP2 = parseInt(obstetricHistory?.para_p2) || 0;
                          const spouseParaP1 = parseInt(spouseObstetricHistory?.para_p1) || 0;
                          const spouseParaP2 = parseInt(spouseObstetricHistory?.para_p2) || 0;
                          
                          // Show if: (para_p1 OR para_p2 has value) AND (at least one para > 0)
                          const hasValidParaValues = (
                            (patientParaP1 > 0 || patientParaP2 > 0) ||
                            (spouseParaP1 > 0 || spouseParaP2 > 0)
                          );
                          
                          const obstetricDataExists = miscarriageExists || infertilityExists;
                          
                          // Don't show if primary infertility, or if no valid para values for non-primary types
                          const shouldShowObstetricHistory = obstetricDataExists && !isPrimary && hasValidParaValues;

                          return shouldShowObstetricHistory ? (
                            <SectionCard>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '8px', flexWrap: 'wrap' }}>
                                <Typography className="section-title" sx={{ mb: 0 }}>Obstetric History&nbsp;
                                {(() => {
                                  // Determine which para to display (patient first, then spouse)
                                  const displayParaP1 = patientParaP1 > 0 ? patientParaP1 : spouseParaP1;
                                  const displayParaP2 = patientParaP2 > 0 ? patientParaP2 : spouseParaP2;

                                  return (
                                     <>[ P{displayParaP1}+{displayParaP2} ]</>
                                  );
                                })()}
                                </Typography>
                              </Box>

                              {/* Miscarriage History Table */}
                              {(() => {
                                const hasMiscarriage = (miscarriageHistories && miscarriageHistories.length > 0) || 
                                                      (spouseMiscarriageHistories && spouseMiscarriageHistories.length > 0);
                                
                                if (!hasMiscarriage) return null;
                                
                                return (
                                  <>
                                    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                      <DataTable size="small">
                                        <TableBody>
                                          <TableRow>
                                            <TableCell className="patient-info-label" sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', textAlign: 'center' }}>
                                              Para
                                            </TableCell>
                                            <TableCell className="patient-info-label" sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', textAlign: 'center' }}>
                                              Weeks
                                            </TableCell>
                                            <TableCell className="patient-info-label" sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', textAlign: 'left' }}>
                                              Type
                                            </TableCell>
                                            <TableCell className="patient-info-label" sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', textAlign: 'center' }}>
                                              D&E
                                            </TableCell>
                                            <TableCell className="patient-info-label" sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', textAlign: 'center' }}>
                                              Year
                                            </TableCell>
                                            <TableCell className="patient-info-label" sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', textAlign: 'left' }}>
                                              Remarks
                                            </TableCell>
                                          </TableRow>
                                          {(() => {
                                            const records = [];
                                            let paraCounter = 1;
                                      
                                            // Process patient miscarriage histories
                                            if (miscarriageHistories && miscarriageHistories.length > 0) {
                                              // Sort by para_number first
                                              const sortedPatientMiscarriages = [...miscarriageHistories].sort((a, b) => {
                                                const paraA = a.para_number || '';
                                                const paraB = b.para_number || '';
                                                const numA = parseInt(paraA.replace(/\D/g, '')) || 0;
                                                const numB = parseInt(paraB.replace(/\D/g, '')) || 0;
                                                return numA - numB;
                                              });

                                              sortedPatientMiscarriages.forEach(miscarriage => {
                                                records.push({
                                                  source: 'Patient',
                                                  para: miscarriage.para_number || `P${paraCounter}`,
                                                  yearOfMiscarriage: miscarriage.year_of_miscarriage || '-',
                                                  typeOfMiscarriage: miscarriage.param_group_member_option?.value || '-',
                                                  wksOfMiscarriage: miscarriage.weeks_of_miscarriage || '-',
                                                  deDone: miscarriage.de_done,
                                                  miscarriageRemarks: miscarriage.remarks || '-'
                                                });
                                                paraCounter++;
                                              });
                                            }
                                            
                                            // Process spouse miscarriage histories
                                            if (spouseMiscarriageHistories && spouseMiscarriageHistories.length > 0) {
                                              // Sort by para_number first
                                              const sortedSpouseMiscarriages = [...spouseMiscarriageHistories].sort((a, b) => {
                                                const paraA = a.para_number || '';
                                                const paraB = b.para_number || '';
                                                const numA = parseInt(paraA.replace(/\D/g, '')) || 0;
                                                const numB = parseInt(paraB.replace(/\D/g, '')) || 0;
                                                return numA - numB;
                                              });

                                              sortedSpouseMiscarriages.forEach(miscarriage => {
                                                records.push({
                                                  source: 'Spouse',
                                                  para: miscarriage.para_number || `P${paraCounter}`,
                                                  yearOfMiscarriage: miscarriage.year_of_miscarriage || '-',
                                                  typeOfMiscarriage: miscarriage.param_group_member_option?.value || '-',
                                                  wksOfMiscarriage: miscarriage.weeks_of_miscarriage || '-',
                                                  deDone: miscarriage.de_done,
                                                  miscarriageRemarks: miscarriage.remarks || '-'
                                                });
                                                paraCounter++;
                                              });
                                            }
                                            
                                            return records.length > 0 ? records.map((record, index) => (
                                              <TableRow key={index}>
                                                {/* Para (Serial Number) */}
                                                <TableCell sx={{ fontSize: '10px', textAlign: 'center', fontWeight: '600', color: '#128296' }}>
                                                  {record.para}
                                                </TableCell>
                                                {/* Weeks */}
                                                <TableCell sx={{ fontSize: '10px', textAlign: 'center' }}>
                                                  {record.wksOfMiscarriage}
                                                </TableCell>
                                                {/* Type Of Miscarriage */}
                                                <TableCell sx={{ fontSize: '10px', textAlign: 'left', color: record.source === 'Patient' ? '#00798E' : '#4caf50', fontWeight: '600' }}>
                                                  {record.typeOfMiscarriage}
                                                </TableCell>
                                                {/* D&E Done */}
                                                <TableCell sx={{ fontSize: '10px', textAlign: 'center' }}>
                                                  {isDEDisabledForType(record.typeOfMiscarriage) ? '-' : 
                                                   (record.deDone === true || record.deDone === 'true' ? 'Yes' : 'No')}
                                                </TableCell>
                                                {/* Year */}
                                                <TableCell sx={{ fontSize: '10px', textAlign: 'center' }}>
                                                  {record.yearOfMiscarriage}
                                                </TableCell>
                                                {/* Miscarriage Remarks */}
                                                <TableCell sx={{ fontSize: '9px', textAlign: 'left' }}>
                                                  {record.miscarriageRemarks !== '-' ? (
                                                    <div dangerouslySetInnerHTML={{ __html: record.miscarriageRemarks }} />
                                                  ) : '-'}
                                                </TableCell>
                                              </TableRow>
                                            )) : (
                                              <TableRow>
                                                <TableCell colSpan={6} sx={{ textAlign: 'center', fontStyle: 'italic', color: '#666', fontSize: '10px' }}>
                                                  No miscarriage records found
                                                </TableCell>
                                              </TableRow>
                                            );
                                          })()}
                                        </TableBody>
                                      </DataTable>
                                    </TableContainer>
                                  </>
                                );
                              })()}
                            </SectionCard>
                          ) : null;
                        })()}

                        {/* Combined Continuous Patient & Spouse Details Section */}
                        {(() => {
                          // Define slug to display name mapping for generic history fields
                          const GENERIC_HISTORY_SLUGS = [
                            'history-common-params-previous-history',
                            'history-common-params-family-history',
                            'history-common-params-allergic-to-drug',
                            'history-common-params-drug-medical-history',
                            'history-common-params-prev-infertility-treatment',
                            'history-common-params-note',
                          ];

                          // Check if there's any data to show
                          const hasPatientMenstrual = menstrualHistories && menstrualHistories.length > 0;
                          const hasSpouseMenstrual = spouseMenstrualHistories && spouseMenstrualHistories.length > 0;
                          const semenData = contactHistory.filter(item =>
                            item.source === maleSource &&
                            item.param_group_member?.slug === 'male-investigation-params-semen-analysis'
                          );
                          
                          const patientMedicalHistoryExists = contactHistory.some(item => {
                            if (item.source !== 'Patient') return false;
                            const hasRelevantParam = GENERIC_HISTORY_SLUGS.includes(item.param_group_member?.slug);
                            if (!hasRelevantParam) return false;
                            const value = item.param_group_member_option?.value || item.value;
                            return value && value.trim() && value.trim() !== '' && value.trim().toLowerCase() !== 'n/a' && value.trim().toLowerCase() !== 'none';
                          });
                          
                          const spouseExists = !!getSpouseInfo(patientInfo);
                          const spouseMedicalHistoryExists = spouseExists && contactHistory.some(item => {
                            if (item.source !== 'Spouse') return false;
                            const hasRelevantParam = GENERIC_HISTORY_SLUGS.includes(item.param_group_member?.slug);
                            if (!hasRelevantParam) return false;
                            const value = item.param_group_member_option?.value || item.value;
                            return value && value.trim() && value.trim() !== '' && value.trim().toLowerCase() !== 'n/a' && value.trim().toLowerCase() !== 'none';
                          });
                          
                          const patientSurgicalHistory = contactHistory.filter(item => 
                            item.source === 'Patient' && 
                            (item.param_group_member?.slug === 'primary-history-common-params-surgical-history')
                          );
                          
                          const spouseSurgicalHistory = contactHistory.filter(item => 
                            item.source === 'Spouse' && 
                            (item.param_group_member?.slug === 'primary-history-common-params-surgical-history')
                          );
                          
                          const patientInvestigations = contactHistory.filter(item => 
                            item.source === 'Patient' && 
                            item.param_group_member?.slug &&
                            (item.param_group_member.slug.includes('investigation') ||
                             item.param_group_member.slug.includes('hsg') ||
                             item.param_group_member.slug.includes('usg'))
                          );
                          
                          const spouseInvestigations = contactHistory.filter(item => 
                            item.source === 'Spouse' && 
                            item.param_group_member?.slug &&
                            (item.param_group_member.slug.includes('investigation') ||
                             item.param_group_member.slug.includes('hsg') ||
                             item.param_group_member.slug.includes('usg')) &&
                            item.param_group_member.slug !== 'male-investigation-params-semen-analysis'
                          );
                          
                          const hasAnyData = hasPatientMenstrual || hasSpouseMenstrual || semenData.length > 0 ||
                            patientMedicalHistoryExists || spouseMedicalHistoryExists ||
                            patientSurgicalHistory.length > 0 || spouseSurgicalHistory.length > 0 ||
                            patientInvestigations.length > 0 || spouseInvestigations.length > 0;
                          
                          if (!hasAnyData) return null;
                          
                          // Check if spouse has any data
                          const spouseSurgicalHistoryExists = spouseExists && contactHistory.filter(item => 
                            item.source === 'Spouse' && 
                            (item.param_group_member?.slug === 'primary-history-common-params-surgical-history')
                          ).length > 0;
                          
                          const spouseInvestigationsData = contactHistory.filter(item => 
                            item.source === 'Spouse' && 
                            item.param_group_member?.slug &&
                            (item.param_group_member.slug.includes('investigation') ||
                             item.param_group_member.slug.includes('hsg') ||
                             item.param_group_member.slug.includes('usg')) &&
                            item.param_group_member.slug !== 'male-investigation-params-semen-analysis'
                          );
                          
                          const hasSpouseData = spouseMedicalHistoryExists || spouseSurgicalHistoryExists || (maleSource === 'Spouse' && semenData.length > 0) || spouseInvestigationsData.length > 0 || hasSpouseMenstrual;
                          
                          return (
                            <SectionCard>
                              <Grid container spacing={2} sx={{ 
                                '@media print': {
                                  display: 'flex',
                                  flexDirection: 'row',
                                  width: '100%',
                                  pageBreakBefore: 'avoid !important',
                                  pageBreakAfter: 'auto !important',
                                  pageBreakInside: 'auto !important',
                                  breakBefore: 'avoid !important',
                                  breakAfter: 'auto !important',
                                  breakInside: 'auto !important'
                                }
                              }}>
                                {/* LEFT COLUMN - PATIENT DATA (CONTINUOUS) */}
                                <Grid item xs={12} md={hasSpouseData ? 6 : 12} sx={{ 
                                  '@media print': {
                                    width: hasSpouseData ? '50%' : '100%',
                                    maxWidth: hasSpouseData ? '50%' : '100%',
                                    flexBasis: hasSpouseData ? '50%' : '100%',
                                    display: 'block'
                                  }
                                }}>
                                  <Typography 
                                    className="party-title"
                                    variant="h6" 
                                    sx={{ 
                                      fontSize: '14px', 
                                      fontWeight: 'bold', 
                                      color: '#00798E', 
                                      mb: 2,
                                      textAlign: 'center',
                                      backgroundColor: '#e0f7fa',
                                      padding: '10px',
                                      borderRadius: '4px',
                                      border: '2px solid #00798E'
                                    }}
                                  >
                                    PATIENT
                                  </Typography>

                                  {/* PATIENT: Menstrual History */}
                                  {hasPatientMenstrual && (
                                    <Box mb={3}>
                                      <Typography className="section-subtitle" variant="subtitle1" sx={{ fontWeight: 'bold', color: '#00798E', fontSize: '12px', mb: 1, borderBottom: '2px solid #00798E', paddingBottom: '4px' }}>
                                        Menstrual History
                                      </Typography>
                                      {(() => {
                                        const latestRecord = menstrualHistories[menstrualHistories.length - 1];
                                        return (
                                          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                            <DataTable size="small">
                                              <TableBody>
                                                <TableRow>
                                                  <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '20%', backgroundColor: '#f8f9fa' }}>LMP</TableCell>
                                                  <TableCell sx={{ fontSize: '10px', width: '30%' }}>
                                                    {latestRecord.lmp ? formatDate(latestRecord.lmp) : '-'}
                                                    {/* <Chip label="Latest" size="small" sx={{ ml: 1, fontSize: '8px', height: '16px', backgroundColor: '#4caf50', color: 'white' }} /> */}
                                                  </TableCell>
                                                  <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '20%', backgroundColor: '#f8f9fa' }}>
                                                    {latestRecord.menarche ? 'Menarche' : ''}
                                                  </TableCell>
                                                  <TableCell sx={{ fontSize: '10px', width: '30%' }}>
                                                    {latestRecord.menarche || ''}
                                                  </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                  <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', backgroundColor: '#f8f9fa' }}>Cycle Length</TableCell>
                                                  <TableCell sx={{ fontSize: '10px' }}>{latestRecord.cycleLength || '-'}</TableCell>
                                                  <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', backgroundColor: '#f8f9fa' }}>Duration</TableCell>
                                                  <TableCell sx={{ fontSize: '10px' }}>{latestRecord.duration || '-'}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                  <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', backgroundColor: '#f8f9fa' }}>Flow</TableCell>
                                                  <TableCell sx={{ fontSize: '10px' }}>{latestRecord.flow || '-'}</TableCell>
                                                  <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', backgroundColor: '#f8f9fa' }}>Pain</TableCell>
                                                  <TableCell sx={{ fontSize: '10px' }}>{latestRecord.pain || '-'}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                  <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', backgroundColor: '#f8f9fa', width: '20%' }}>Remarks</TableCell>
                                                  <TableCell colSpan={3} sx={{ fontSize: '10px' }}>
                                                    {latestRecord.other ? <Box sx={{ '& p, & ul, & ol': { margin: 0, padding: 0 } }} dangerouslySetInnerHTML={{ __html: latestRecord.other }} /> : '-'}
                                                  </TableCell>
                                                </TableRow>
                                              </TableBody>
                                            </DataTable>
                                          </TableContainer>
                                        );
                                      })()}
                                    </Box>
                                  )}

                                  {/* PATIENT: Medical History */}
                                  {(() => {
                                    // Define slug to display name mapping for generic history fields
                                    const GENERIC_HISTORY_FIELDS = [
                                      { slug: 'history-common-params-previous-history', displayName: 'Personal History' },
                                      { slug: 'history-common-params-family-history', displayName: 'Family History' },
                                      { slug: 'history-common-params-prev-infertility-treatment', displayName: 'Previous Infertility Treatment' },
                                      { slug: 'history-common-params-allergic-to-drug', displayName: 'Allergic to Drugs' },
                                      { slug: 'history-common-params-drug-medical-history', displayName: 'Drug History' },
                                      { slug: 'history-common-params-note', displayName: 'Notes' },
                                    ];

                                    const patientHistoryExists = contactHistory.some(item => 
                                      item.source === 'Patient' && 
                                      GENERIC_HISTORY_FIELDS.some(field => item.param_group_member?.slug === field.slug) &&
                                      (item.param_group_member_option?.value?.trim() || item.value?.trim())
                                    );

                                    return patientHistoryExists ? (
                                      <Box mb={3}>
                                        <Typography className="section-subtitle" variant="subtitle1" sx={{ fontWeight: 'bold', color: '#00798E', fontSize: '12px', mb: 1, borderBottom: '2px solid #00798E', paddingBottom: '4px' }}>
                                          Medical History
                                        </Typography>
                                        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0', overflowX: 'visible' }}>
                                          <DataTable size="small">
                                            <TableBody>
                                              {GENERIC_HISTORY_FIELDS.map(field => {
                                                const fieldData = contactHistory
                                                  .filter(item => item.source === 'Patient' && item.param_group_member?.slug === field.slug)
                                                  .map(item => item.param_group_member_option?.value || item.value)
                                                  .filter(value => value && value.trim())
                                                  .join(', ');
                                                
                                                return fieldData ? (
                                                  <TableRow key={field.slug}>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', width: '40%', fontSize: '10px', backgroundColor: '#f8f9fa', verticalAlign: 'top' }}>
                                                      {field.displayName}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '10px', wordWrap: 'break-word', whiteSpace: 'normal', verticalAlign: 'top' }}>
                                                      <Box dangerouslySetInnerHTML={{ __html: fieldData }} sx={{ '& p, & ul, & ol': { margin: 0 }, wordBreak: 'break-word' }} />
                                                    </TableCell>
                                                  </TableRow>
                                                ) : null;
                                              })}
                                            </TableBody>
                                          </DataTable>
                                        </TableContainer>
                                      </Box>
                                    ) : null;
                                  })()}

                                  {/* PATIENT: Surgical History */}
                                  {(() => {
                                    const patientSurgicalHistory = contactHistory.filter(item => 
                                      item.source === 'Patient' && 
                                      (item.param_group_member?.slug === 'primary-history-common-params-surgical-history' ||
                                       item.param_group_member?.slug === 'surgical-history' ||
                                       item.param_group_member?.name === 'Surgical History')
                                    );

                                    return patientSurgicalHistory.length > 0 ? (
                                      <Box mb={3}>
                                        <Typography className="section-subtitle" variant="subtitle1" sx={{ fontWeight: 'bold', color: '#00798E', fontSize: '12px', mb: 1, borderBottom: '2px solid #00798E', paddingBottom: '4px' }}>
                                          Surgical History
                                        </Typography>
                                        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                          <DataTable size="small">
                                            <TableBody>
                                              <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '25%', backgroundColor: '#f8f9fa' }}>Date</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '35%', backgroundColor: '#f8f9fa' }}>Surgery Name</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '40%', backgroundColor: '#f8f9fa' }}>Remarks</TableCell>
                                              </TableRow>
                                              {patientSurgicalHistory.map((item, index) => (
                                                <TableRow key={index}>
                                                  <TableCell sx={{ fontSize: '10px' }}>
                                                    {item.history_date ? formatDate(item.history_date) : '-'}
                                                  </TableCell>
                                                  <TableCell sx={{ fontSize: '10px' }}>
                                                    {item.param_group_member_option?.value || item.param_group_member?.name || item.name || '-'}
                                                  </TableCell>
                                                  <TableCell sx={{ fontSize: '10px' }}>
                                                    <Box dangerouslySetInnerHTML={{ __html: item.remarks || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} />
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </DataTable>
                                        </TableContainer>
                                      </Box>
                                    ) : null;
                                  })()}

                                  {/* PATIENT: Semen Analysis (only when the patient is the male party) */}
                                  {maleSource === 'Patient' && semenData.length > 0 && (
                                    <Box mb={3}>
                                      <Typography className="section-subtitle" variant="subtitle1" sx={{ fontWeight: 'bold', color: '#00798E', fontSize: '12px', mb: 1, borderBottom: '2px solid #00798E', paddingBottom: '4px' }}>
                                        Semen Analysis
                                      </Typography>
                                      {(() => {
                                        const groupedByDate = {};
                                        semenData.forEach(item => {
                                          const date = item.history_date ? formatDate(item.history_date) : 'Unknown Date';
                                          if (!groupedByDate[date]) groupedByDate[date] = [];
                                          groupedByDate[date].push(item);
                                        });

                                        const sortSemenItems = (items) => {
                                          return items.sort((a, b) => {
                                            const idA = a.param_group_member_option_id || a.irm_param_group_member_options_id || Infinity;
                                            const idB = b.param_group_member_option_id || b.irm_param_group_member_options_id || Infinity;
                                            return idA - idB;
                                          });
                                        };

                                        return Object.keys(groupedByDate).sort((a, b) => {
                                          if (a === 'Unknown Date') return 1;
                                          if (b === 'Unknown Date') return -1;
                                          const parseDate = (dateStr) => {
                                            const [day, month, year] = dateStr.split('/');
                                            return new Date(year, month - 1, day);
                                          };
                                          return parseDate(b) - parseDate(a);
                                        }).map((date, groupIndex) => {
                                          const sortedItems = sortSemenItems(groupedByDate[date]);

                                          return (
                                            <Box key={groupIndex} mb={2}>
                                              <Typography variant="subtitle2" sx={{ fontWeight: '700', color: '#128296', fontSize: '10px', mb: 0.5, backgroundColor: '#f8f9fa', padding: '4px 8px', borderRadius: '3px', border: '1px solid #e0e0e0' }}>
                                                Date: {date}
                                              </Typography>
                                              <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                                <DataTable size="small">
                                                  <TableBody>
                                                    <TableRow>
                                                      <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '40%' }}>Type</TableCell>
                                                      <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '35%' }}>Value</TableCell>
                                                      <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '25%' }}>Remarks</TableCell>
                                                    </TableRow>
                                                    {sortedItems.map((item, index) => (
                                                      <TableRow key={index}>
                                                        <TableCell sx={{ fontSize: '10px' }}>{item.param_group_member_option?.value || item.param_group_member?.name || '-'}</TableCell>
                                                        <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.value || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                        <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.remarks || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                      </TableRow>
                                                    ))}
                                                  </TableBody>
                                                </DataTable>
                                              </TableContainer>
                                            </Box>
                                          );
                                        });
                                      })()}
                                    </Box>
                                  )}

                                  {/* PATIENT: Investigations */}
                                  {(() => {
                                    const patientInvestigations = contactHistory.filter(item =>
                                      item.source === 'Patient' &&
                                      (item.param_group_member?.slug?.startsWith('blood-investigation-params-') ||
                                       (patientGenderCode === 'M'
                                         ? item.param_group_member?.slug?.startsWith('male-investigation-params-')
                                         : item.param_group_member?.slug?.startsWith('female-investigation-params-'))) &&
                                      item.param_group_member?.slug !== 'male-investigation-params-semen-analysis'
                                    );

                                    const categorizeInvestigations = (investigations) => {
                                      const categories = {
                                        hormonal: [],
                                        serology: [],
                                        biochemistry: [],
                                        usg: [],
                                        hsg: [],
                                        sis: [],
                                        radiology: []
                                      };

                                      investigations.forEach(item => {
                                        const slug = item.param_group_member?.slug || '';

                                        if (slug === 'blood-investigation-params-hormonal') {
                                          categories.hormonal.push(item);
                                        } else if (slug === 'blood-investigation-params-serology') {
                                          categories.serology.push(item);
                                        } else if (slug === 'blood-investigation-params-bio-chemistry') {
                                          categories.biochemistry.push(item);
                                        } else if (slug === 'female-investigation-params-usg') {
                                          categories.usg.push(item);
                                        } else if (slug === 'female-investigation-params-hsg-sis' && item.param_group_member_option?.value === 'HSG') {
                                          categories.hsg.push(item);
                                        } else if (slug === 'female-investigation-params-hsg-sis' && item.param_group_member_option?.value === 'SIS') {
                                          categories.sis.push(item);
                                        } else if (slug === 'female-investigation-params-radiology') {
                                          categories.radiology.push(item);
                                        } else if (slug === 'male-investigation-params-radiology') {
                                          categories.radiology.push(item);
                                        }
                                      });

                                      return categories;
                                    };

                                    const categories = categorizeInvestigations(patientInvestigations);
                                    const hasAnyCategory = Object.values(categories).some(cat => cat.length > 0);

                                    return hasAnyCategory ? (
                                      <Box mb={3}>
                                        <Typography className="section-subtitle" variant="subtitle1" sx={{ fontWeight: 'bold', color: '#00798E', fontSize: '12px', mb: 1, borderBottom: '2px solid #00798E', paddingBottom: '4px' }}>
                                          Investigations
                                        </Typography>
                                        
                                        {/* Hormonal */}
                                        {categories.hormonal.length > 0 && (
                                          <Box mb={2}>
                                            <Typography className="section-subtitle" variant="subtitle2" sx={{ fontWeight: 'bold', color: '#128296', fontSize: '11px', mb: 0.5, backgroundColor: '#e0f7fa', padding: '4px 8px', borderRadius: '3px' }}>
                                              Hormonal
                                            </Typography>
                                            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                              <DataTable size="small">
                                                <TableBody>
                                                  <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '20%', backgroundColor: '#f8f9fa' }}>Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '30%', backgroundColor: '#f8f9fa' }}>Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '20%', backgroundColor: '#f8f9fa' }}>Value</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', backgroundColor: '#f8f9fa' }}>Remarks</TableCell>
                                                  </TableRow>
                                                  {categories.hormonal.map((item, index) => (
                                                    <TableRow key={index}>
                                                      <TableCell sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>{item.history_date ? formatDate(item.history_date) : '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}>{item?.name || item.param_group_member?.name || item.param_group_member_option?.value || '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.value || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.remarks || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </DataTable>
                                            </TableContainer>
                                          </Box>
                                        )}

                                        {/* Serology */}
                                        {categories.serology.length > 0 && (
                                          <Box mb={2}>
                                            <Typography className="section-subtitle" variant="subtitle2" sx={{ fontWeight: 'bold', color: '#128296', fontSize: '11px', mb: 0.5, backgroundColor: '#e0f7fa', padding: '4px 8px', borderRadius: '3px' }}>
                                              Serology
                                            </Typography>
                                            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                              <DataTable size="small">
                                                <TableBody>
                                                  <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '20%', backgroundColor: '#f8f9fa' }}>Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '30%', backgroundColor: '#f8f9fa' }}>Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '20%', backgroundColor: '#f8f9fa' }}>Value</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', backgroundColor: '#f8f9fa' }}>Remarks</TableCell>
                                                  </TableRow>
                                                  {categories.serology.map((item, index) => (
                                                    <TableRow key={index}>
                                                      <TableCell sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>{item.history_date ? formatDate(item.history_date) : '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}>{item?.name || item.param_group_member?.name || item.param_group_member_option?.value || '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.value || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.remarks || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </DataTable>
                                            </TableContainer>
                                          </Box>
                                        )}

                                        {/* Biochemistry */}
                                        {categories.biochemistry.length > 0 && (
                                          <Box mb={2}>
                                            <Typography className="section-subtitle" variant="subtitle2" sx={{ fontWeight: 'bold', color: '#128296', fontSize: '11px', mb: 0.5, backgroundColor: '#e0f7fa', padding: '4px 8px', borderRadius: '3px' }}>
                                              Biochemistry
                                            </Typography>
                                            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                              <DataTable size="small">
                                                <TableBody>
                                                  <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '20%', backgroundColor: '#f8f9fa' }}>Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '30%', backgroundColor: '#f8f9fa' }}>Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '20%', backgroundColor: '#f8f9fa' }}>Value</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', backgroundColor: '#f8f9fa' }}>Remarks</TableCell>
                                                  </TableRow>
                                                  {categories.biochemistry.map((item, index) => (
                                                    <TableRow key={index}>
                                                      <TableCell sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>{item.history_date ? formatDate(item.history_date) : '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}>{item?.name || item.param_group_member?.name || item.param_group_member_option?.value || '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.value || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.remarks || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </DataTable>
                                            </TableContainer>
                                          </Box>
                                        )}

                                        {/* USG */}
                                        {categories.usg.length > 0 && (
                                          <Box mb={2}>
                                            <Typography className="section-subtitle" variant="subtitle2" sx={{ fontWeight: 'bold', color: '#128296', fontSize: '11px', mb: 0.5, backgroundColor: '#e0f7fa', padding: '4px 8px', borderRadius: '3px' }}>
                                              USG
                                            </Typography>
                                            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                              <DataTable size="small">
                                                <TableBody>
                                                  <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '20%', backgroundColor: '#f8f9fa' }}>Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '30%', backgroundColor: '#f8f9fa' }}>Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', backgroundColor: '#f8f9fa' }}>Remarks</TableCell>
                                                  </TableRow>
                                                  {categories.usg.map((item, index) => (
                                                    <TableRow key={index}>
                                                      <TableCell sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>{item.history_date ? formatDate(item.history_date) : '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}>{item?.name || item.param_group_member?.name || item.param_group_member_option?.value || '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.remarks || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </DataTable>
                                            </TableContainer>
                                          </Box>
                                        )}

                                        {/* HSG */}
                                        {categories.hsg.length > 0 && (
                                          <Box mb={2}>
                                            <Typography className="section-subtitle" variant="subtitle2" sx={{ fontWeight: 'bold', color: '#128296', fontSize: '11px', mb: 0.5, backgroundColor: '#e0f7fa', padding: '4px 8px', borderRadius: '3px' }}>
                                              HSG
                                            </Typography>
                                            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                              <DataTable size="small">
                                                <TableBody>
                                                  <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '20%', backgroundColor: '#f8f9fa' }}>Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '30%', backgroundColor: '#f8f9fa' }}>Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', backgroundColor: '#f8f9fa' }}>Remarks</TableCell>
                                                  </TableRow>
                                                  {categories.hsg.map((item, index) => (
                                                    <TableRow key={index}>
                                                      <TableCell sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>{item.history_date ? formatDate(item.history_date) : '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}>{item?.name || item.param_group_member?.name || item.param_group_member_option?.value || '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.remarks || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </DataTable>
                                            </TableContainer>
                                          </Box>
                                        )}

                                        {/* SIS */}
                                        {categories.sis.length > 0 && (
                                          <Box mb={2}>
                                            <Typography className="section-subtitle" variant="subtitle2" sx={{ fontWeight: 'bold', color: '#128296', fontSize: '11px', mb: 0.5, backgroundColor: '#e0f7fa', padding: '4px 8px', borderRadius: '3px' }}>
                                              SIS
                                            </Typography>
                                            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                              <DataTable size="small">
                                                <TableBody>
                                                  <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '20%', backgroundColor: '#f8f9fa' }}>Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '30%', backgroundColor: '#f8f9fa' }}>Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', backgroundColor: '#f8f9fa' }}>Remarks</TableCell>
                                                  </TableRow>
                                                  {categories.sis.map((item, index) => (
                                                    <TableRow key={index}>
                                                      <TableCell sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>{item.history_date ? formatDate(item.history_date) : '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}>{item?.name || item.param_group_member?.name || item.param_group_member_option?.value || '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.remarks || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </DataTable>
                                            </TableContainer>
                                          </Box>
                                        )}

                                        {/* Other Investigations */}
                                        {categories.radiology.length > 0 && (
                                          <Box mb={2}>
                                            <Typography className="section-subtitle" variant="subtitle2" sx={{ fontWeight: 'bold', color: '#128296', fontSize: '11px', mb: 0.5, backgroundColor: '#e0f7fa', padding: '4px 8px', borderRadius: '3px' }}>
                                              Other Investigations
                                            </Typography>
                                            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                              <DataTable size="small">
                                                <TableBody>
                                                  <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '20%', backgroundColor: '#f8f9fa' }}>Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', width: '30%', backgroundColor: '#f8f9fa' }}>Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', fontSize: '10px', backgroundColor: '#f8f9fa' }}>Remarks</TableCell>
                                                  </TableRow>
                                                  {categories.radiology.map((item, index) => (
                                                    <TableRow key={index}>
                                                      <TableCell sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>{item.history_date ? formatDate(item.history_date) : '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}>{item?.name || item.param_group_member?.name || item.param_group_member_option?.value || '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.remarks || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </DataTable>
                                            </TableContainer>
                                          </Box>
                                        )}
                                      </Box>
                                    ) : null;
                                  })()}

                                </Grid>

                                {/* RIGHT COLUMN - SPOUSE DATA (CONTINUOUS) - ONLY IF SPOUSE HAS DATA */}
                                {hasSpouseData && (
                                <Grid item xs={12} md={6} sx={{ 
                                  '@media print': {
                                    width: '50%',
                                    maxWidth: '50%',
                                    flexBasis: '50%',
                                    display: 'block'
                                  }
                                }}>
                                  <Typography 
                                    className="party-title"
                                    variant="h6" 
                                    sx={{ 
                                      fontSize: '14px', 
                                      fontWeight: 'bold', 
                                      color: '#4caf50', 
                                      mb: 2,
                                      textAlign: 'center',
                                      backgroundColor: '#e8f5e9',
                                      padding: '10px',
                                      borderRadius: '4px',
                                      border: '2px solid #4caf50'
                                    }}
                                  >
                                    SPOUSE
                                  </Typography>

                                  {/* SPOUSE: Menstrual History (only when the spouse is the female party) */}
                                  {hasSpouseMenstrual && (
                                    <Box mb={3}>
                                      <Typography className="section-subtitle" variant="subtitle1" sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '12px', mb: 1, borderBottom: '2px solid #4caf50', paddingBottom: '4px' }}>
                                        Menstrual History
                                      </Typography>
                                      {(() => {
                                        const latestRecord = spouseMenstrualHistories[spouseMenstrualHistories.length - 1];
                                        return (
                                          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                            <DataTable size="small">
                                              <TableBody>
                                                <TableRow>
                                                  <TableCell sx={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '10px', width: '20%', backgroundColor: '#f1f8f4' }}>LMP</TableCell>
                                                  <TableCell sx={{ fontSize: '10px', width: '30%' }}>
                                                    {latestRecord.lmp ? formatDate(latestRecord.lmp) : '-'}
                                                  </TableCell>
                                                  <TableCell sx={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '10px', width: '20%', backgroundColor: '#f1f8f4' }}>
                                                    {latestRecord.menarche ? 'Menarche' : ''}
                                                  </TableCell>
                                                  <TableCell sx={{ fontSize: '10px', width: '30%' }}>
                                                    {latestRecord.menarche || ''}
                                                  </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                  <TableCell sx={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '10px', backgroundColor: '#f1f8f4' }}>Cycle Length</TableCell>
                                                  <TableCell sx={{ fontSize: '10px' }}>{latestRecord.cycleLength || '-'}</TableCell>
                                                  <TableCell sx={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '10px', backgroundColor: '#f1f8f4' }}>Duration</TableCell>
                                                  <TableCell sx={{ fontSize: '10px' }}>{latestRecord.duration || '-'}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                  <TableCell sx={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '10px', backgroundColor: '#f1f8f4' }}>Flow</TableCell>
                                                  <TableCell sx={{ fontSize: '10px' }}>{latestRecord.flow || '-'}</TableCell>
                                                  <TableCell sx={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '10px', backgroundColor: '#f1f8f4' }}>Pain</TableCell>
                                                  <TableCell sx={{ fontSize: '10px' }}>{latestRecord.pain || '-'}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                  <TableCell sx={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '10px', backgroundColor: '#f1f8f4', width: '20%' }}>Remarks</TableCell>
                                                  <TableCell colSpan={3} sx={{ fontSize: '10px' }}>
                                                    {latestRecord.other ? <Box sx={{ '& p, & ul, & ol': { margin: 0, padding: 0 } }} dangerouslySetInnerHTML={{ __html: latestRecord.other }} /> : '-'}
                                                  </TableCell>
                                                </TableRow>
                                              </TableBody>
                                            </DataTable>
                                          </TableContainer>
                                        );
                                      })()}
                                    </Box>
                                  )}

                                  {/* SPOUSE: Semen Analysis (only when the spouse is the male party) */}
                                  {maleSource === 'Spouse' && semenData.length > 0 && (
                                    <Box mb={3}>
                                      <Typography className="section-subtitle" variant="subtitle1" sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '12px', mb: 1, borderBottom: '2px solid #4caf50', paddingBottom: '4px' }}>
                                        Semen Analysis
                                      </Typography>
                                      {(() => {
                                        const groupedByDate = {};
                                        semenData.forEach(item => {
                                          const date = item.history_date ? formatDate(item.history_date) : 'Unknown Date';
                                          if (!groupedByDate[date]) groupedByDate[date] = [];
                                          groupedByDate[date].push(item);
                                        });
                                        
                                        const parameterOrder = [
                                          'volume',
                                          'liquifaction', 
                                          'liquefaction',
                                          'total semen count',
                                          'morphology',
                                          'motility',
                                          'remarks'
                                        ];
                                        
                                        const sortSemenItems = (items) => {
                                          return items.sort((a, b) => {
                                            // Sort by param_group_member_option_id in ascending order
                                            const idA = a.param_group_member_option_id || a.irm_param_group_member_options_id || Infinity;
                                            const idB = b.param_group_member_option_id || b.irm_param_group_member_options_id || Infinity;
                                            return idA - idB;
                                          });
                                        };
                                        
                                        return Object.keys(groupedByDate).sort((a, b) => {
                                          if (a === 'Unknown Date') return 1;
                                          if (b === 'Unknown Date') return -1;
                                          const parseDate = (dateStr) => {
                                            const [day, month, year] = dateStr.split('/');
                                            return new Date(year, month - 1, day);
                                          };
                                          return parseDate(b) - parseDate(a);
                                        }).map((date, groupIndex) => {
                                          const sortedItems = sortSemenItems(groupedByDate[date]);
                                          
                                          return (
                                            <Box key={groupIndex} mb={2}>
                                              <Typography variant="subtitle2" sx={{ fontWeight: '700', color: '#2e7d32', fontSize: '10px', mb: 0.5, backgroundColor: '#f1f8f4', padding: '4px 8px', borderRadius: '3px', border: '1px solid #c8e6c9' }}>
                                                Date: {date}
                                              </Typography>
                                              <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                                <DataTable size="small">
                                                  <TableBody>
                                                    <TableRow>
                                                      <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '40%' }}>Type</TableCell>
                                                      <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '35%' }}>Value</TableCell>
                                                      <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '25%' }}>Remarks</TableCell>
                                                    </TableRow>
                                                    {sortedItems.map((item, index) => (
                                                      <TableRow key={index}>
                                                        <TableCell sx={{ fontSize: '10px' }}>{item.param_group_member_option?.value || item.param_group_member?.name || '-'}</TableCell>
                                                        <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.value || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                        <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.remarks || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                      </TableRow>
                                                    ))}
                                                  </TableBody>
                                                </DataTable>
                                              </TableContainer>
                                            </Box>
                                          );
                                        });
                                      })()}
                                    </Box>
                                  )}

                                  {/* SPOUSE: Investigations */}
                                  {(() => {
                                    const spouseInvestigations = contactHistory.filter(item =>
                                      item.source === 'Spouse' &&
                                      (item.param_group_member?.slug?.startsWith('blood-investigation-params-') ||
                                       (spouseGenderCode === 'M'
                                         ? item.param_group_member?.slug?.startsWith('male-investigation-params-')
                                         : item.param_group_member?.slug?.startsWith('female-investigation-params-'))) &&
                                      item.param_group_member?.slug !== 'male-investigation-params-semen-analysis'
                                    );

                                    const categorizeInvestigations = (investigations) => {
                                      const categories = {
                                        hormonal: [],
                                        serology: [],
                                        biochemistry: [],
                                        usg: [],
                                        hsg: [],
                                        sis: [],
                                        radiology: []
                                      };

                                      investigations.forEach(item => {
                                        const slug = item.param_group_member?.slug || '';

                                        if (slug === 'blood-investigation-params-hormonal') {
                                          categories.hormonal.push(item);
                                        } else if (slug === 'blood-investigation-params-serology') {
                                          categories.serology.push(item);
                                        } else if (slug === 'blood-investigation-params-bio-chemistry') {
                                          categories.biochemistry.push(item);
                                        } else if (slug === 'female-investigation-params-usg') {
                                          categories.usg.push(item);
                                        } else if (slug === 'female-investigation-params-hsg-sis' && item.param_group_member_option?.value === 'HSG') {
                                          categories.hsg.push(item);
                                        } else if (slug === 'female-investigation-params-hsg-sis' && item.param_group_member_option?.value === 'SIS') {
                                          categories.sis.push(item);
                                        } else if (slug === 'female-investigation-params-radiology') {
                                          categories.radiology.push(item);
                                        } else if (slug === 'male-investigation-params-radiology') {
                                          categories.radiology.push(item);
                                        }
                                      });

                                      return categories;
                                    };

                                    const categories = categorizeInvestigations(spouseInvestigations);
                                    const hasAnyCategory = Object.values(categories).some(cat => cat.length > 0);

                                    return hasAnyCategory ? (
                                      <Box mb={3}>
                                        <Typography className="section-subtitle" variant="subtitle1" sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '12px', mb: 1, borderBottom: '2px solid #4caf50', paddingBottom: '4px' }}>
                                          Investigations
                                        </Typography>
                                        
                                        {/* Hormonal */}
                                        {categories.hormonal.length > 0 && (
                                          <Box mb={2}>
                                            <Typography className="section-subtitle" variant="subtitle2" sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '11px', mb: 0.5, backgroundColor: '#e8f5e9', padding: '4px 8px', borderRadius: '3px' }}>
                                              Hormonal
                                            </Typography>
                                            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                              <DataTable size="small">
                                                <TableBody>
                                                  <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '20%', backgroundColor: '#f8f9fa' }}>Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '30%', backgroundColor: '#f8f9fa' }}>Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '20%', backgroundColor: '#f8f9fa' }}>Value</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', backgroundColor: '#f8f9fa' }}>Remarks</TableCell>
                                                  </TableRow>
                                                  {categories.hormonal.map((item, index) => (
                                                    <TableRow key={index}>
                                                      <TableCell sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>{item.history_date ? formatDate(item.history_date) : '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}>{item?.name || item.param_group_member?.name || item.param_group_member_option?.value || '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.value || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.remarks || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </DataTable>
                                            </TableContainer>
                                          </Box>
                                        )}

                                        {/* Serology */}
                                        {categories.serology.length > 0 && (
                                          <Box mb={2}>
                                            <Typography className="section-subtitle" variant="subtitle2" sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '11px', mb: 0.5, backgroundColor: '#e8f5e9', padding: '4px 8px', borderRadius: '3px' }}>
                                              Serology
                                            </Typography>
                                            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                              <DataTable size="small">
                                                <TableBody>
                                                  <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '20%', backgroundColor: '#f8f9fa' }}>Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '30%', backgroundColor: '#f8f9fa' }}>Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '20%', backgroundColor: '#f8f9fa' }}>Value</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', backgroundColor: '#f8f9fa' }}>Remarks</TableCell>
                                                  </TableRow>
                                                  {categories.serology.map((item, index) => (
                                                    <TableRow key={index}>
                                                      <TableCell sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>{item.history_date ? formatDate(item.history_date) : '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}>{item?.name || item.param_group_member?.name || item.param_group_member_option?.value || '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.value || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.remarks || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </DataTable>
                                            </TableContainer>
                                          </Box>
                                        )}

                                        {/* Biochemistry */}
                                        {categories.biochemistry.length > 0 && (
                                          <Box mb={2}>
                                            <Typography className="section-subtitle" variant="subtitle2" sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '11px', mb: 0.5, backgroundColor: '#e8f5e9', padding: '4px 8px', borderRadius: '3px' }}>
                                              Biochemistry
                                            </Typography>
                                            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                              <DataTable size="small">
                                                <TableBody>
                                                  <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '20%', backgroundColor: '#f8f9fa' }}>Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '30%', backgroundColor: '#f8f9fa' }}>Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '20%', backgroundColor: '#f8f9fa' }}>Value</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', backgroundColor: '#f8f9fa' }}>Remarks</TableCell>
                                                  </TableRow>
                                                  {categories.biochemistry.map((item, index) => (
                                                    <TableRow key={index}>
                                                      <TableCell sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>{item.history_date ? formatDate(item.history_date) : '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}>{item?.name || item.param_group_member?.name || item.param_group_member_option?.value || '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.value || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.remarks || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </DataTable>
                                            </TableContainer>
                                          </Box>
                                        )}

                                        {/* USG */}
                                        {categories.usg.length > 0 && (
                                          <Box mb={2}>
                                            <Typography className="section-subtitle" variant="subtitle2" sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '11px', mb: 0.5, backgroundColor: '#e8f5e9', padding: '4px 8px', borderRadius: '3px' }}>
                                              USG
                                            </Typography>
                                            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                              <DataTable size="small">
                                                <TableBody>
                                                  <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '20%', backgroundColor: '#f8f9fa' }}>Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '30%', backgroundColor: '#f8f9fa' }}>Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', backgroundColor: '#f8f9fa' }}>Remarks</TableCell>
                                                  </TableRow>
                                                  {categories.usg.map((item, index) => (
                                                    <TableRow key={index}>
                                                      <TableCell sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>{item.history_date ? formatDate(item.history_date) : '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}>{item?.name || item.param_group_member?.name || item.param_group_member_option?.value || '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.remarks || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </DataTable>
                                            </TableContainer>
                                          </Box>
                                        )}

                                        {/* HSG */}
                                        {categories.hsg.length > 0 && (
                                          <Box mb={2}>
                                            <Typography className="section-subtitle" variant="subtitle2" sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '11px', mb: 0.5, backgroundColor: '#e8f5e9', padding: '4px 8px', borderRadius: '3px' }}>
                                              HSG
                                            </Typography>
                                            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                              <DataTable size="small">
                                                <TableBody>
                                                  <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '20%', backgroundColor: '#f8f9fa' }}>Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '30%', backgroundColor: '#f8f9fa' }}>Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', backgroundColor: '#f8f9fa' }}>Remarks</TableCell>
                                                  </TableRow>
                                                  {categories.hsg.map((item, index) => (
                                                    <TableRow key={index}>
                                                      <TableCell sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>{item.history_date ? formatDate(item.history_date) : '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}>{item?.name || item.param_group_member?.name || item.param_group_member_option?.value || '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.remarks || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </DataTable>
                                            </TableContainer>
                                          </Box>
                                        )}

                                        {/* SIS */}
                                        {categories.sis.length > 0 && (
                                          <Box mb={2}>
                                            <Typography className="section-subtitle" variant="subtitle2" sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '11px', mb: 0.5, backgroundColor: '#e8f5e9', padding: '4px 8px', borderRadius: '3px' }}>
                                              SIS
                                            </Typography>
                                            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                              <DataTable size="small">
                                                <TableBody>
                                                  <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '20%', backgroundColor: '#f8f9fa' }}>Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '30%', backgroundColor: '#f8f9fa' }}>Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', backgroundColor: '#f8f9fa' }}>Remarks</TableCell>
                                                  </TableRow>
                                                  {categories.sis.map((item, index) => (
                                                    <TableRow key={index}>
                                                      <TableCell sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>{item.history_date ? formatDate(item.history_date) : '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}>{item?.name || item.param_group_member?.name || item.param_group_member_option?.value || '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.remarks || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </DataTable>
                                            </TableContainer>
                                          </Box>
                                        )}

                                        {/* Radiology */}
                                        {categories.radiology.length > 0 && (
                                          <Box mb={2}>
                                            <Typography className="section-subtitle" variant="subtitle2" sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '11px', mb: 0.5, backgroundColor: '#e8f5e9', padding: '4px 8px', borderRadius: '3px' }}>
                                              Radiology
                                            </Typography>
                                            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                              <DataTable size="small">
                                                <TableBody>
                                                  <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '20%', backgroundColor: '#f8f9fa' }}>Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '30%', backgroundColor: '#f8f9fa' }}>Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '20%', backgroundColor: '#f8f9fa' }}>Value</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', backgroundColor: '#f8f9fa' }}>Remarks</TableCell>
                                                  </TableRow>
                                                  {categories.radiology.map((item, index) => (
                                                    <TableRow key={index}>
                                                      <TableCell sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>{item.history_date ? formatDate(item.history_date) : '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}>{item?.name || item.param_group_member?.name || item.param_group_member_option?.value || '-'}</TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.value || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                      <TableCell sx={{ fontSize: '10px' }}><Box dangerouslySetInnerHTML={{ __html: item.remarks || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} /></TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </DataTable>
                                            </TableContainer>
                                          </Box>
                                        )}
                                      </Box>
                                    ) : null;
                                  })()}
                                  {/* SPOUSE: Medical History */}
                                  {(() => {
                                    // Define slug to display name mapping for generic history fields
                                    const GENERIC_HISTORY_FIELDS = [
                                      { slug: 'history-common-params-previous-history', displayName: 'Personal History' },
                                      { slug: 'history-common-params-family-history', displayName: 'Family History' },
                                      { slug: 'history-common-params-prev-infertility-treatment', displayName: 'Previous Infertility Treatment' },
                                      { slug: 'history-common-params-allergic-to-drug', displayName: 'Allergic to Drugs' },
                                      { slug: 'history-common-params-drug-medical-history', displayName: 'Drug History' },
                                      { slug: 'history-common-params-note', displayName: 'Notes' },
                                    ];

                                    const spouseExists = !!getSpouseInfo(patientInfo);
                                    const spouseHistoryExists = spouseExists && contactHistory.some(item => 
                                      item.source === 'Spouse' && 
                                      GENERIC_HISTORY_FIELDS.some(field => item.param_group_member?.slug === field.slug) &&
                                      (item.param_group_member_option?.value?.trim() || item.value?.trim())
                                    );

                                    return spouseHistoryExists ? (
                                      <Box mb={3}>
                                        <Typography className="section-subtitle" variant="subtitle1" sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '12px', mb: 1, borderBottom: '2px solid #4caf50', paddingBottom: '4px' }}>
                                          Medical History
                                        </Typography>
                                        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0', overflowX: 'visible' }}>
                                          <DataTable size="small">
                                            <TableBody>
                                              {GENERIC_HISTORY_FIELDS.map(field => {
                                                const fieldData = contactHistory
                                                  .filter(item => item.source === 'Spouse' && item.param_group_member?.slug === field.slug)
                                                  .map(item => item.param_group_member_option?.value || item.value)
                                                  .filter(value => value && value.trim())
                                                  .join(', ');
                                                
                                                return fieldData ? (
                                                  <TableRow key={field.slug}>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', width: '40%', fontSize: '10px', backgroundColor: '#f8f9fa', verticalAlign: 'top' }}>
                                                      {field.displayName}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '10px', wordWrap: 'break-word', whiteSpace: 'normal', verticalAlign: 'top' }}>
                                                      <Box dangerouslySetInnerHTML={{ __html: fieldData }} sx={{ '& p, & ul, & ol': { margin: 0 }, wordBreak: 'break-word' }} />
                                                    </TableCell>
                                                  </TableRow>
                                                ) : null;
                                              })}
                                            </TableBody>
                                          </DataTable>
                                        </TableContainer>
                                      </Box>
                                    ) : null;
                                  })()}

                                  {/* SPOUSE: Surgical History */}
                                  {(() => {
                                    const spouseSurgicalHistory = contactHistory.filter(item => 
                                      item.source === 'Spouse' && 
                                      (item.param_group_member?.slug === 'primary-history-common-params-surgical-history')
                                    );

                                    return spouseSurgicalHistory.length > 0 ? (
                                      <Box mb={3}>
                                        <Typography className="section-subtitle" variant="subtitle1" sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '12px', mb: 1, borderBottom: '2px solid #4caf50', paddingBottom: '4px' }}>
                                          Surgical History
                                        </Typography>
                                        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                          <DataTable size="small">
                                            <TableBody>
                                              <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '25%', backgroundColor: '#f8f9fa' }}>Date</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '35%', backgroundColor: '#f8f9fa' }}>Surgery Name</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: '10px', width: '40%', backgroundColor: '#f8f9fa' }}>Remarks</TableCell>
                                              </TableRow>
                                              {spouseSurgicalHistory.map((item, index) => (
                                                <TableRow key={index}>
                                                  <TableCell sx={{ fontSize: '10px' }}>
                                                    {item.history_date ? formatDate(item.history_date) : '-'}
                                                  </TableCell>
                                                  <TableCell sx={{ fontSize: '10px' }}>
                                                    {item.param_group_member_option?.value || item.param_group_member?.name || item.name || '-'}
                                                  </TableCell>
                                                  <TableCell sx={{ fontSize: '10px' }}>
                                                    <Box dangerouslySetInnerHTML={{ __html: item.remarks || '-' }} sx={{ '& p, & ul, & ol': { margin: 0 } }} />
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </DataTable>
                                        </TableContainer>
                                      </Box>
                                    ) : null;
                                  })()}


                                </Grid>
                                )}

                              </Grid>
                            </SectionCard>
                          );
                        })()}

                        {/* Comorbidities — always shown (NIL when nothing recorded
                            in any visit); mirrors the page's two-column layout:
                            Patient + Spouse side-by-side (half each) when a spouse
                            exists, full width otherwise. */}
                        {(() => {
                          const comorbiditiesBox = (value, color) => (
                            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0', overflowX: 'visible' }}>
                              <DataTable size="small">
                                <TableBody>
                                  <TableRow>
                                    <TableCell sx={{ fontSize: '10px', wordWrap: 'break-word', whiteSpace: 'normal', verticalAlign: 'top', borderLeft: `3px solid ${color}` }}>
                                      {value ? (
                                        <Box
                                          dangerouslySetInnerHTML={{ __html: value }}
                                          sx={{ '& p, & ul, & ol': { margin: 0 }, wordBreak: 'break-word' }}
                                        />
                                      ) : 'NIL'}
                                    </TableCell>
                                  </TableRow>
                                </TableBody>
                              </DataTable>
                            </TableContainer>
                          );

                          return (
                            <SectionCard>
                              <Typography className="section-title" sx={{ mb: '8px' }}>Comorbidities</Typography>
                              <Grid container spacing={2} sx={{
                                '@media print': {
                                  display: 'flex',
                                  flexDirection: 'row',
                                  width: '100%',
                                  pageBreakInside: 'auto !important',
                                  breakInside: 'auto !important',
                                }
                              }}>
                                <Grid item xs={12} md={hasSpouse ? 6 : 12} sx={{
                                  '@media print': {
                                    width: hasSpouse ? '50%' : '100%',
                                    maxWidth: hasSpouse ? '50%' : '100%',
                                    flexBasis: hasSpouse ? '50%' : '100%',
                                    display: 'block'
                                  }
                                }}>
                                  <Typography
                                    className="party-title"
                                    variant="h6"
                                    sx={{ fontSize: '14px', fontWeight: 'bold', color: '#00798E', mb: 1, textAlign: 'center', backgroundColor: '#e0f7fa', padding: '8px', borderRadius: '4px', border: '2px solid #00798E' }}
                                  >
                                    PATIENT
                                  </Typography>
                                  {comorbiditiesBox(patientComorbidities, '#00798E')}
                                </Grid>

                                {hasSpouse && (
                                  <Grid item xs={12} md={6} sx={{
                                    '@media print': {
                                      width: '50%',
                                      maxWidth: '50%',
                                      flexBasis: '50%',
                                      display: 'block'
                                    }
                                  }}>
                                    <Typography
                                      className="party-title"
                                      variant="h6"
                                      sx={{ fontSize: '14px', fontWeight: 'bold', color: '#4caf50', mb: 1, textAlign: 'center', backgroundColor: '#e8f5e9', padding: '8px', borderRadius: '4px', border: '2px solid #4caf50' }}
                                    >
                                      SPOUSE
                                    </Typography>
                                    {comorbiditiesBox(spouseComorbidities, '#4caf50')}
                                  </Grid>
                                )}
                              </Grid>
                            </SectionCard>
                          );
                        })()}

                      </InfertilitySummaryContainer>
                    ) : null}
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Floating Action Button for Print */}
      {!loading && !loadingVisitData && patientInfo && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Tooltip title="Print Infertility Summary" placement="left">
            <Fab
              color="primary"
              onClick={handlePrint}
              sx={{
                backgroundColor: '#00bcd4',
                '&:hover': {
                  backgroundColor: '#00acc1',
                },
                width: 64,
                height: 64,
              }}
            >
              <PrintIcon sx={{ fontSize: '2rem' }} />
            </Fab>
          </Tooltip>

          <Tooltip title="Download as PDF" placement="left">
            <Fab
              color="secondary"
              onClick={() => console.log("Download clicked")}
              sx={{
                backgroundColor: '#4caf50',
                '&:hover': {
                  backgroundColor: '#45a049',
                },
                width: 64,
                height: 64,
              }}
            >
              <DownloadIcon sx={{ fontSize: '2rem' }} />
            </Fab>
          </Tooltip>
        </Box>
      )}
      
      <Footer />
    </DashboardLayout>
  );
}

export default InfertilitySummary; 
